import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah, STATUS_LABEL, STATUS_COLOR, periodeLabel, canBillingManage, canBillingAdminOnly, receivedLabel, methodLabel } from "@/lib/billing";
import { currentWibPeriode } from "@/lib/time";
import { INVOICE_PRINT_STYLE, RECEIPT_PRINT_STYLE, buildNbInvoiceSection, openNbReceipt } from "@/lib/invoicePrint";
import { NbInvoiceDialog } from "@/components/billing/NbInvoiceDialog";
import { NbInvoiceTableRows, NbInvoiceCards } from "@/components/billing/NbInvoiceRows";
import { NbBulkActionDialog } from "@/components/billing/NbBulkActionDialog";
import { Plus, FileText, Percent, Ban, XCircle, FileDown, Wallet, Upload, X, Image as ImageIcon, Camera, Trash2, Printer, ReceiptText, Pencil, Loader2, CalendarClock } from "lucide-react";
import { AutoInvoiceDialog } from "@/components/billing/AutoInvoiceDialog";
import { highlightMatch } from "@/lib/highlight";

// Sep 2026 Phase 23 — `PdfInvoiceButton` lama dihapus.
// Detail Invoice sekarang memakai pipeline PDF final Phase 22x/22y via
// prop callback `onOpenInvoicePdf` (saveRowPdf dari BillingInvoicesPage).
// Kwitansi PDF di Riwayat Pembayaran memakai `onOpenReceipt` (openReceipt).

/**
 * BillingInvoices — halaman utama Invoice.
 * Admin/Billing: list + generate + detail. Diskon & cancel: ADMIN only.
 * Kolektor: list scoped ke customer assigned (backend enforced).
 */

// Feb 2026 — Format tanggal pembayaran terakhir (zona lokal, no time).
// Backend mengirim ISO datetime di `last_payment_date`; null jika invoice
// belum punya payment CONFIRMED.
function formatPaymentDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("id-ID", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return "—"; }
}

const overdueCls = (d) => d > 90 ? "bg-[#7F1D1D] text-white" : d > 60 ? "bg-[#DC2626] text-white"
  : d > 30 ? "bg-[#F97316] text-white" : "bg-[#FDE047] text-[#713F12]";
const OverdueBadge = ({ inv, tid = "billing-inv-overdue" }) => (inv.days_overdue == null
  ? <span className="text-[#9CA3AF]">—</span>
  : <span className="inline-flex flex-col items-start gap-0.5">
      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap ${overdueCls(inv.days_overdue)}`}
            title="Umur komponen tunggakan tertua pada kategori Aging terpilih (termasuk bon carry-forward)"
            data-testid={`${tid}-${inv.invoice_id}`}>{inv.days_overdue} hari</span>
      {inv.aging_amount != null && Number(inv.aging_amount) !== Number(inv.sisa_tagihan) && (
        <span className="text-[10px] font-mono text-[#6B7280] whitespace-nowrap" title="Bagian sisa yang masuk kategori Aging ini"
              data-testid={`${tid}-amount-${inv.invoice_id}`}>{formatRupiah(inv.aging_amount)} tertunggak</span>
      )}
    </span>);
const BLN_SHORT = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
// Tanggal tersimpan apa adanya (YYYY-MM-DD) → "31 Okt 2026"; null bila tidak valid.
const fmtDue = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ""));
  if (!m || +m[2] < 1 || +m[2] > 12 || +m[3] < 1 || +m[3] > 31) return null;
  return `${m[3]} ${BLN_SHORT[+m[2] - 1]} ${m[1]}`;
};
const DueDateCell = ({ inv, tid = "billing-inv-due" }) => {
  const raw = inv.tanggal_jatuh_tempo;
  const txt = fmtDue(raw);
  const oldest = inv.aging_oldest_due && String(inv.aging_oldest_due).slice(0, 10) !== String(raw || "").slice(0, 10)
    ? fmtDue(inv.aging_oldest_due) : null;
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      {txt
        ? <span className="whitespace-nowrap" data-testid={`${tid}-${inv.invoice_id}`}>{txt}</span>
        : <span className="whitespace-nowrap text-[#DC2626] font-semibold" title={raw ? `Nilai tersimpan: ${raw}` : "Jatuh tempo kosong"}
                data-testid={`${tid}-${inv.invoice_id}`}>— {raw ? "Tidak valid" : "Kosong"}</span>}
      {oldest && (
        <span className="text-[10px] text-[#B45309] whitespace-nowrap" title="Jatuh tempo bon tertua di rantai carry-forward (dipakai Aging)"
              data-testid={`${tid}-oldest-${inv.invoice_id}`}>Bon tertua: {oldest}</span>
      )}
    </span>
  );
};
const AgeCell = ({ c }) => {
  if (c.tertunggak <= 0) return <span className="text-[10px] font-semibold text-[#6B7280] whitespace-nowrap">Lunas (dialokasikan)</span>;
  if (c.days_overdue) return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap ${overdueCls(c.days_overdue)}`}>{c.days_overdue} hari</span>;
  return <span className="text-[10px] text-[#2563EB] whitespace-nowrap">{c.bucket === "tanpa_jatuh_tempo" ? "Tanpa jatuh tempo" : "Belum jatuh tempo"}</span>;
};
const BonComponentRow = ({ c }) => {
  const lunas = c.tertunggak <= 0;
  const effDiff = c.jatuh_tempo_efektif && c.jatuh_tempo_efektif !== String(c.tanggal_jatuh_tempo || "").slice(0, 10);
  return (
    <tr className={`border-t ${lunas ? "text-[#9CA3AF] bg-[#F9FAFB]" : ""}`} data-testid={`bon-comp-row-${c.invoice_id}`}>
      <td className="px-2 py-1.5">
        <div className="font-semibold whitespace-nowrap">{periodeLabel(c.periode_tagihan)}{c.is_current && <span className="ml-1 text-[10px] font-normal">(bulan ini)</span>}</div>
        <div className="font-mono text-[10px]">{c.invoice_id}</div>
      </td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        <div>{fmtDue(c.tanggal_jatuh_tempo) || "— Kosong"}</div>
        {effDiff && <div className="text-[10px] text-[#B45309]" title="Dibatasi ke jatuh tempo paling awal di rantai (dipakai Aging)">efektif: {fmtDue(c.jatuh_tempo_efektif)}</div>}
      </td>
      <td className="px-2 py-1.5 text-right font-mono">{formatRupiah(c.nominal_awal)}</td>
      <td className="px-2 py-1.5 text-right font-mono">{formatRupiah(c.dialokasikan)}</td>
      <td className={`px-2 py-1.5 text-right font-mono ${lunas ? "" : "font-bold"}`} data-testid={`bon-comp-tertunggak-${c.invoice_id}`}>{formatRupiah(c.tertunggak)}</td>
      <td className="px-2 py-1.5" data-testid={`bon-comp-age-${c.invoice_id}`}><AgeCell c={c} /></td>
    </tr>
  );
};
const BonComponents = ({ data }) => {
  if (!data) return null;
  if (!data.reconciled) {
    return (
      <div className="border border-[#F59E0B] bg-[#FFFBEB] rounded p-2 text-xs text-[#92400E]" data-testid="bon-comp-unreconciled">
        <b>Rincian bon tidak dapat direkonsiliasi.</b> {data.reason}. Aging memakai jatuh tempo invoice ini untuk seluruh sisa; data historis tidak diubah.
      </div>
    );
  }
  const comps = data.components || [];
  const sumSisa = comps.reduce((a, c) => a + c.tertunggak, 0);
  return (
    <div className="border rounded" data-testid="bon-comp-section">
      <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#4B5563] border-b bg-[#F8FAFC]">
        Rincian Komponen Bon <span className="normal-case font-normal tracking-normal">· posisi {fmtDue(data.as_of)} · pembayaran dialokasikan ke utang tertua (FIFO)</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase text-[#6B7280]">
            <tr>
              <th className="px-2 py-1 text-left">Periode asal</th><th className="px-2 py-1 text-left">Jatuh tempo</th>
              <th className="px-2 py-1 text-right">Nominal awal</th><th className="px-2 py-1 text-right">Dialokasikan</th>
              <th className="px-2 py-1 text-right">Tertunggak</th><th className="px-2 py-1 text-left">Umur</th>
            </tr>
          </thead>
          <tbody>{comps.map((c) => <BonComponentRow key={c.invoice_id} c={c} />)}</tbody>
          <tfoot className="border-t-2 font-semibold">
            <tr>
              <td className="px-2 py-1.5" colSpan={4}>Total sisa (komponen tertunggak)</td>
              <td className="px-2 py-1.5 text-right font-mono" data-testid="bon-comp-total-sisa">{formatRupiah(sumSisa)}</td><td />
            </tr>
            <tr className="text-[#B91C1C]">
              <td className="px-2 py-1.5" colSpan={4}>Sudah lewat jatuh tempo (masuk Aging)</td>
              <td className="px-2 py-1.5 text-right font-mono" data-testid="bon-comp-total-overdue">{formatRupiah(data.total_tertunggak)}</td><td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
const AGING_LABEL = { "1_30": "1–30 hari", "31_60": "31–60 hari", "61_90": "61–90 hari", gt_90: "> 90 hari", overdue: "Semua tunggakan" };
const INV_STATUS_URL_OPTIONS = ["BELUM_LUNAS", "SEBAGIAN", "LUNAS", "DIBATALKAN", "SEBAGIAN,BELUM_LUNAS", "LUNAS,SEBAGIAN,BELUM_LUNAS"];

export default function BillingInvoices() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const reqSeq = useRef(0);
  // Okt 2026 — Filter awal dari URL (drill-down KPI Dashboard Billing), dibaca sekali saat mount.
  const [searchParams, setSearchParams] = useSearchParams();
  // Default: bulan berjalan WIB, kecuali URL membawa periode, atau drill-down Aging (lintas periode).
  const [periode, setPeriode] = useState(() => {
    const v = searchParams.get("periode") || "";
    if (/^\d{4}-\d{2}$/.test(v)) return v;
    return searchParams.get("aging") ? "" : currentWibPeriode();
  });
  const [status, setStatus] = useState(() => {
    const v = searchParams.get("status") || "";
    return INV_STATUS_URL_OPTIONS.includes(v) ? v : "";
  });
  // Okt 2026 — Drill-down Aging dari Dashboard Billing (dibaca sekali dari URL).
  const [aging, setAging] = useState(() => {
    const v = searchParams.get("aging") || "";
    return AGING_LABEL[v] ? v : "";
  });
  const [agingAsOf] = useState(() => {
    const v = searchParams.get("aging_as_of") || "";
    return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : "";
  });
  const [agingSum, setAgingSum] = useState(null);
  const [agingRef, setAgingRef] = useState("");
  // Feb 2026 — Filter nama customer (free-text, case-insensitive partial).
  // Backward-compat var-name tetap `customerId*` supaya diff minimal; semantik
  // UI & query param sudah bergeser ke "cari NAMA customer" (lebih ergonomis
  // untuk operasional). Backend terima param baru `customer_name_q`.
  const [customerId, setCustomerId] = useState("");
  // Feb 2026 — Filter kolektor (ADMIN ONLY). Kosong = "Semua Kolektor"
  // (behavior existing tetap). Backend enforce RBAC; kolektor tidak
  // dapat menyalahgunakan parameter ini karena scope-nya sudah dikunci.
  const [collectorId, setCollectorId] = useState("");
  const [collectorList, setCollectorList] = useState([]);
  // Feb 2026 — Filter wilayah (ADMIN ONLY, konsisten dgn Customer Billing).
  const [wilayahId, setWilayahId] = useState("");
  const [wilayahList, setWilayahList] = useState([]);
  // Feb 2026 — Filter status_layanan customer untuk follow-up ISOLIR (ADMIN ONLY).
  const [customerStatus, setCustomerStatus] = useState("");
  // Feb 2026 — Sortable Wilayah/Kolektor (client-side, aktif per page).
  const [sortBy, setSortBy] = useState(""); // "wilayah_id" | "current_collector_name" | ""
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"
  // Sep 2026 Phase 22n — debounce 400ms untuk customerId filter supaya
  // load() auto-fire setelah user berhenti mengetik.
  const [customerIdDeb, setCustomerIdDeb] = useState("");
  useEffect(() => {
    const h = setTimeout(() => setCustomerIdDeb(customerId), 400);
    return () => clearTimeout(h);
  }, [customerId]);
  // Sep 2026 Phase 21 — Toggle "Tampilkan invoice ter-konsolidasi". Default
  // OFF: hanya invoice aktif (carry_forward_to=None) yang tampil. ON:
  // include_carried=true → tampilkan histori invoice ter-carry-forward untuk
  // audit. Business logic tidak berubah.
  const [includeCarried, setIncludeCarried] = useState(false);
  const [detail, setDetail] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
  const [pageSize, setPageSize] = useState(10);
  const canManage = canBillingManage(user);
  const isAdmin = canBillingAdminOnly(user);
  const [showNb, setShowNb] = useState(false);
  const [nbOpenId, setNbOpenId] = useState(null);
  // Invoice Barang/Jasa: daftar terpisah, tidak ikut total/perhitungan invoice bulanan.
  const [nbRows, setNbRows] = useState([]);
  const [nbTotal, setNbTotal] = useState(0);
  const [jenis, setJenis] = useState(""); // "" semua | "BULANAN" | "NB"
  const [showAutoInv, setShowAutoInv] = useState(() => isAdmin && searchParams.get("autoinv") === "1");
  useEffect(() => {
    if (!searchParams.has("autoinv")) return;
    const sp = new URLSearchParams(searchParams); sp.delete("autoinv");
    setSearchParams(sp, { replace: true });
    if (isAdmin) setShowAutoInv(true);
  }, [searchParams, isAdmin, setSearchParams]);

  // Sep 2026 Phase 19 — Bulk delete selection state. Berisi Set of
  // invoice_id yang dipilih user. Reset saat filter berubah agar UX
  // konsisten (user tidak menghapus invoice yang tidak lagi tampil).
  const [selected, setSelected] = useState(() => new Set());
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelect = (invId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(invId)) next.delete(invId);
      else next.add(invId);
      return next;
    });
  };
  // Pilihan Invoice Barang/Jasa disimpan terpisah (id NB) agar Bayar/Void Pembayaran tidak menyentuhnya.
  const [nbSel, setNbSel] = useState(() => new Set());
  const [nbBulk, setNbBulk] = useState(null); // "delete" | "void"
  const toggleNbSelect = (id) => setNbSel((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allSelected = rows.length + nbRows.length > 0 && rows.every((r) => selected.has(r.invoice_id)) && nbRows.every((r) => nbSel.has(r.id));
  const toggleSelectAll = () => {
    if (allSelected) { setSelected(new Set()); setNbSel(new Set()); return; }
    setSelected(new Set(rows.map((r) => r.invoice_id)));
    setNbSel(new Set(nbRows.map((r) => r.id)));
  };
  const clearSelection = () => { setSelected(new Set()); setNbSel(new Set()); };
  const selectionCount = selected.size;
  const printCount = selected.size + nbSel.size;
  // Sep 2026 Phase 22r — Bulk Print. Fetch detail invoice + branding
  // secara sequential (idempotent, hanya GET — tidak mutate), render ke
  // hidden container, panggil window.print(). Setiap invoice dipisahkan
  // page-break-after: always.
  const [printing, setPrinting] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);
  // Sep 2026 Phase 22t — Track per-row PDF generation. Menyimpan
  // invoice_id yang sedang di-generate agar tombol PDF pada row lain
  // ikut disabled (mencegah double-generation yang membebani browser).
  const [pdfRowId, setPdfRowId] = useState(null);

  // Sep 2026 Phase 23a — Bulk Bayar (ADMIN ONLY). Loop POST individual
  // `/billing/payments` supaya tetap memanfaatkan RBAC & workflow
  // existing (proper receiver assignment, status transition, snapshot).
  // Frontend tidak menghitung ulang nominal; pakai `pay_full: true`
  // supaya backend = source of truth (cegah race).
  const [showBulkPay, setShowBulkPay] = useState(false);
  const [bulkPaying, setBulkPaying] = useState(false);
  const [bulkPayForm, setBulkPayForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    method: "TUNAI",
    notes: "",
  });
  const [bulkPayResult, setBulkPayResult] = useState(null);
  // Feb 2026 — Bulk Void Payment Multi Invoice (ADMIN ONLY).
  // Reuse `_void_single_payment()` existing via endpoint
  // `/billing/payments/bulk-void-multi-invoice`. Preview via dry_run.
  const [showBulkVoidMulti, setShowBulkVoidMulti] = useState(false);

  // Sep 2026 Phase 22s — HTML builder di-share antara bulk print &
  // save-as-PDF. Zero mutation, pure fetch → render.
  // Phase 22t: accept optional `ids` param; default ke selection.
  // Phase 22u: fetch customer detail (alamat, no_hp) + 4 field company
  // info dari settings/invoice-branding; template disesuaikan mengikuti
  // referensi visual final (2-column layout, tanpa "AROMANET System
  // Billing", tanpa "STATUS: BELUM LUNAS", paket tanpa Mbps).
  const buildInvoiceBatchHtml = async (ids = null, nbIds = null) => {
    const invoiceIds = ids || Array.from(selected);
    const nbInvoiceIds = nbIds || (ids ? [] : Array.from(nbSel));
    const [{ data: branding }, ...invResps] = await Promise.all([
      api.get("/settings/invoice-branding").catch(() => ({ data: {} })),
      ...invoiceIds.map((id) => api.get(`/billing/invoices/${id}`)),
      ...nbInvoiceIds.map((id) => api.get(`/billing/nb-invoices/${id}`)),
    ]);
    const all = invResps.map((r) => r.data);
    const invoices = all.slice(0, invoiceIds.length).filter(Boolean);
    const nbInvoices = all.slice(invoiceIds.length).filter(Boolean);
    if (invoices.length + nbInvoices.length === 0) return null;
    // Fetch customer detail (alamat + no_hp) — snapshot tidak menyimpan
    // kedua field ini di invoice, jadi ambil dari billing_customers.
    // Read-only, tidak mutate. Kegagalan 1 customer → alamat/HP kosong,
    // invoice tetap tercetak.
    const uniqCids = Array.from(new Set(invoices.map((i) => i.customer_id).filter(Boolean)));
    const custResps = await Promise.all(uniqCids.map(
      (cid) => api.get(`/billing/customers/${cid}`).then((r) => r.data).catch(() => null)
    ));
    const custMap = new Map();
    uniqCids.forEach((cid, idx) => { custMap.set(cid, custResps[idx] || {}); });

    const rupiah = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    // Strip trailing speed suffix e.g. "Bronze 115 7 Mbps" → "Bronze 115",
    // atau "Silver 10 Mbps" → "Silver". Kompatibel dengan angka desimal.
    const cleanPackageName = (name) => {
      const raw = String(name || "").trim();
      return raw.replace(/\s+\d+(?:[.,]\d+)?\s*Mbps\s*$/i, "").trim() || raw;
    };
    const nl2br = (s) => esc(s).replace(/\n/g, "<br/>");

    const companyName = branding.company_name || "";
    const slogan = branding.slogan || "";
    const companyAddress = branding.company_address || "";
    const companyNpwp = branding.company_npwp || "";
    const companyPhone = branding.company_phone || "";
    const logo = branding.logo_data_url || "";
    const bankName = branding.bank_name || "";
    const bankAcct = branding.bank_account_number || "";
    const bankHolder = branding.bank_account_holder || "";
    const qris = branding.qris_data_url || "";
    const hasPayInfo = !!(bankName || bankAcct || bankHolder || qris);

    const pages = invoices.map((inv) => {
      const cust = custMap.get(inv.customer_id) || {};
      const custAddr = cust.alamat || "";
      const custPhone = cust.no_hp || "";
      const paketDisplay = cleanPackageName(inv.package_name_snapshot);
      const rightInfo = [
        companyAddress ? `<tr><td class="lbl">Alamat</td><td class="sep">:</td><td>${nl2br(companyAddress)}</td></tr>` : "",
        companyNpwp ? `<tr><td class="lbl">NPWP</td><td class="sep">:</td><td>${esc(companyNpwp)}</td></tr>` : "",
        companyPhone ? `<tr><td class="lbl">Telp / WhatsApp</td><td class="sep">:</td><td>${esc(companyPhone)}</td></tr>` : "",
      ].filter(Boolean).join("");
      // Tanggal & periode dalam bahasa Indonesia. Periode di DB berformat
      // "YYYY-MM" → tampilkan sebagai "Agustus 2026".
      const bulanID = ["", "Januari", "Februari", "Maret", "April", "Mei", "Juni",
                       "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
      const tanggalInv = inv.created_at ? formatTanggalId(inv.created_at) : "-";
      const [pY, pM] = String(inv.periode_tagihan || "").split("-");
      const periodeDisplay = (pM && pY) ? `${bulanID[parseInt(pM, 10)] || pM} ${pY}` : String(inv.periode_tagihan || "-");
      const jatuhTempo = inv.tanggal_jatuh_tempo ? formatTanggalId(inv.tanggal_jatuh_tempo) : "-";
      // City dari alamat perusahaan (heuristik "Kab. XXX"), fallback = kosong.
      const cityMatch = /Kab\.\s*([^\n,]+)/i.exec(companyAddress);
      const city = cityMatch ? cityMatch[1].trim() : "";
      const todayId = formatTanggalId(new Date().toISOString().slice(0, 10));
      // Nominal — semua data dari invoice snapshot. Tidak ada PPN.
      const harga = Number(inv.harga_paket_snapshot || 0);
      const diskon = Number(inv.diskon || 0);
      const bonPrev = Number(inv.bon_previous_amount || 0);
      const subtotal = harga; // 1 baris paket × 1 qty
      const totalTagihan = Number(inv.total_harus_dibayar || 0);
      // Printer info dari user yang sedang login (dinamis).
      const roleLabel = ({ admin: "Admin", koord: "Koordinator", kolektor: "Kolektor", teknisi: "Teknisi", spv: "Supervisor" })[user?.role] || (user?.role || "-");
      const printerName = (user?.name || "").trim() || (user?.username || "-");
      const bonRow = bonPrev
        ? `<tr><td class="lbl">Tunggakan Sebelumnya</td><td class="r">${rupiah(bonPrev)}</td></tr>`
        : "";
      // Okt 2026 — Rincian tunggakan per bulan (sumber = Aging carry-forward).
      // Hanya bila terekonsiliasi; komponen lunas (FIFO) & tagihan bulan ini tidak dicetak.
      const bc = inv.bon_components;
      const prevComps = bonPrev && bc && bc.reconciled
        ? (bc.components || []).filter((c) => !c.is_current && Number(c.tertunggak) > 0) : [];
      const fmtD = (d) => (d ? formatTanggalId(String(d).slice(0, 10)) : "—");
      const ketr = (c) => (c.days_overdue ? `Terlambat ${c.days_overdue} hari` : "Belum jatuh tempo");
      const shown = prevComps.slice(0, 6);
      const rest = prevComps.slice(6);
      const bonSum = prevComps.reduce((a, c) => a + Number(c.tertunggak || 0), 0);
      const bonRows = shown.map((c) => `<tr><td>${esc(periodeLabel(c.periode_tagihan))}</td><td class="mono">${esc(c.invoice_id)}</td><td>${esc(fmtD(c.jatuh_tempo_efektif))}</td><td class="r">${rupiah(c.tertunggak)}</td><td>${esc(ketr(c))}</td></tr>`).join("")
        + (rest.length ? `<tr><td colspan="3">Bulan lainnya (${rest.length})</td><td class="r">${rupiah(rest.reduce((a, c) => a + Number(c.tertunggak || 0), 0))}</td><td>${rest.some((c) => c.days_overdue) ? "Terlambat" : "Belum jatuh tempo"}</td></tr>` : "");
      const bonTable = prevComps.length ? `
        <div class="bon-wrap" data-bon-rows="${prevComps.length}">
          <div class="bon-title">Rincian Tunggakan Sebelumnya <span>(posisi ${esc(fmtD(bc.as_of))})</span></div>
          <table class="tbl bon-tbl">
            <thead><tr><th style="width:18%">Periode</th><th style="width:26%">No. Invoice</th><th style="width:18%">Jatuh Tempo</th><th style="width:16%">Sisa Tertunggak</th><th>Keterangan</th></tr></thead>
            <tbody>${bonRows}</tbody>
            <tfoot><tr><td colspan="3" class="r"><b>Total Sisa Tunggakan Sebelumnya</b></td><td class="r"><b>${rupiah(bonSum)}</b></td><td></td></tr></tfoot>
          </table>
          ${bonSum !== bonPrev ? `<div class="bon-note">Pembayaran ${rupiah(bonPrev - bonSum)} telah dialokasikan ke tunggakan tertua.</div>` : ""}
        </div>` : "";
      return `
      <section class="inv-page${prevComps.length ? " has-bon" : ""}">
        <header class="inv-hdr">
          <div class="hdr-left">
            ${logo ? `<div class="logo-wrap"><img src="${esc(logo)}" alt="Logo" class="inv-logo"/></div>` : ""}
            ${slogan ? `<div class="inv-slogan">${esc(slogan)}</div>` : ""}
          </div>
          <div class="hdr-right">
            <div class="inv-brand">${esc(companyName || "AROMA NET")}</div>
            <table class="hdr-info">${rightInfo}</table>
          </div>
        </header>
        <hr class="divider-green"/>
        <div class="inv-title-wrap">
          <div class="inv-title">INVOICE</div>
          <div class="inv-no">No. Invoice: <span class="mono">${esc(inv.invoice_id)}</span></div>
        </div>
        <div class="inv-body">
          <div class="col">
            <div class="section-title">Kepada Yth:</div>
            <table class="kv">
              <tr><td class="lbl">ID Pelanggan</td><td class="sep">:</td><td class="mono">${esc(inv.customer_id)}</td></tr>
              <tr><td class="lbl">Nama Pelanggan</td><td class="sep">:</td><td>${esc(inv.customer_name_snapshot)}</td></tr>
              <tr><td class="lbl">Alamat</td><td class="sep">:</td><td>${custAddr ? nl2br(custAddr) : '<span class="muted">—</span>'}</td></tr>
              <tr><td class="lbl">No. HP</td><td class="sep">:</td><td>${custPhone ? esc(custPhone) : '<span class="muted">—</span>'}</td></tr>
            </table>
          </div>
          <div class="col">
            <div class="section-title">Informasi Invoice</div>
            <table class="kv">
              <tr><td class="lbl">Tanggal Invoice</td><td class="sep">:</td><td>${esc(tanggalInv)}</td></tr>
              <tr><td class="lbl">Periode</td><td class="sep">:</td><td>${esc(periodeDisplay)}</td></tr>
              <tr><td class="lbl">Tgl. Jatuh Tempo</td><td class="sep">:</td><td style="color:#DC2626;font-weight:800;background:#FEF2F2;padding:5px 6px;border-radius:3px">${esc(jatuhTempo)}</td></tr>
            </table>
          </div>
        </div>
        <div class="section-title inv-detail-title">Rincian Tagihan</div>
        <table class="tbl">
          <thead>
            <tr>
              <th style="width:8%">No.</th>
              <th style="width:22%">Paket / Layanan</th>
              <th>Deskripsi</th>
              <th style="width:14%">Harga</th>
              <th style="width:8%">Qty</th>
              <th style="width:16%">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>${esc(paketDisplay)}</td>
              <td>Layanan Internet</td>
              <td class="r">${rupiah(harga)}</td>
              <td>1</td>
              <td class="r">${rupiah(subtotal)}</td>
            </tr>
          </tbody>
        </table>
        <div class="summary-wrap">
          <table class="summary">
            <tr><td class="lbl">Subtotal</td><td class="r">${rupiah(subtotal)}</td></tr>
            ${diskon ? `<tr><td class="lbl">Diskon</td><td class="r">-${rupiah(diskon)}</td></tr>` : `<tr><td class="lbl">Diskon</td><td class="r">${rupiah(0)}</td></tr>`}
            ${bonRow}
            <tr class="total"><td class="lbl">Total Tagihan</td><td class="r">${rupiah(totalTagihan)}</td></tr>
          </table>
        </div>
        ${bonTable}
        <div class="terbilang">
          <div class="lbl">Terbilang</div><div>:</div>
          <div><em>${esc(terbilang(totalTagihan))} Rupiah</em></div>
        </div>
        <div class="pay-info">
          <div class="pay-title">Informasi Pembayaran</div>
          <div class="pay-body">Harap lakukan pembayaran sebelum tanggal jatuh tempo untuk menghindari gangguan layanan. Terima kasih atas kepercayaan Anda kepada kami.</div>
          ${hasPayInfo ? `
          <div class="pay-methods">
            ${(bankName || bankAcct || bankHolder) ? `
            <div class="pay-bank">
              <div class="pay-method-title">Transfer Bank</div>
              <table class="pay-bank-tbl">
                ${bankName ? `<tr><td class="lbl">Bank</td><td class="sep">:</td><td class="val">${esc(bankName)}</td></tr>` : ""}
                ${bankAcct ? `<tr><td class="lbl">No. Rekening</td><td class="sep">:</td><td class="val mono">${esc(bankAcct)}</td></tr>` : ""}
                ${bankHolder ? `<tr><td class="lbl">Atas Nama</td><td class="sep">:</td><td class="val">${esc(bankHolder)}</td></tr>` : ""}
              </table>
            </div>` : ""}
            ${qris ? `
            <div class="pay-qris">
              <div class="pay-method-title">Scan QRIS</div>
              <img src="${esc(qris)}" alt="QRIS" class="qris-img"/>
            </div>` : ""}
          </div>` : ""}
        </div>
        <div class="keterangan">Demikian invoice ini dibuat sebagai tagihan atas layanan yang telah kami berikan.</div>
        <div class="sign-row">
          <div class="sign-left">${city ? esc(city) + ", " : ""}${esc(todayId)}</div>
          <div class="sign-right">
            <div>Hormat kami,</div>
            <div class="role">Dicetak oleh: ${esc(roleLabel)}</div>
            <div class="line"></div>
            <div class="name">( ${esc(printerName)} )</div>
          </div>
        </div>
        <div class="footer-note">Invoice ini dicetak secara otomatis oleh sistem dan sah tanpa tanda tangan basah.</div>
      </section>
    `;
    }).join("") + nbInvoices.map((n) => buildNbInvoiceSection(n, branding, user)).join("");
    const style = INVOICE_PRINT_STYLE;  // dibagi dengan Invoice Non-Bulanan (lib/invoicePrint.js)
    const numbers = [...invoices.map((i) => i.invoice_id), ...nbInvoices.map((n) => n.nomor)];
    return { invoices, numbers, html: `<html><head><title>Invoice ${esc(companyName || "AROMA NET")}</title><style>${style}</style></head><body>${pages}</body></html>` };
  };

  const bulkPrint = async () => {
    if (printing || printCount === 0) return;
    setPrinting(true);
    try {
      const built = await buildInvoiceBatchHtml();
      if (!built) { toast.error("Tidak ada invoice untuk dicetak"); return; }
      const w = window.open("", "_blank", "width=900,height=1200");
      if (!w) { toast.error("Popup diblokir browser. Izinkan popup untuk cetak."); return; }
      w.document.open(); w.document.write(built.html); w.document.close();
      setTimeout(() => { try { w.focus(); w.print(); } catch { /* no-op */ } }, 300);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal menyiapkan cetak");
    } finally { setPrinting(false); }
  };

  // Sep 2026 Phase 22s — Save as PDF via jspdf + html2canvas. Render tiap
  // invoice ke canvas 794×1123px (A4 @ 96dpi), tambahkan ke jsPDF sebagai
  // page baru. Tidak lewat print dialog — file langsung terunduh.
  // Phase 22t: refactor jadi `runSavePdf(ids?)` supaya bisa dipakai
  // bulk (dari selection) dan per-row (dari tombol PDF di kolom Aksi).
  const runSavePdf = async (ids = null, nbIds = null) => {
    let host = null;
    try {
      const built = await buildInvoiceBatchHtml(ids, nbIds);
      if (!built) { toast.error("Tidak ada invoice untuk diunduh"); return; }
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import("jspdf"),
        import("html2canvas"),
      ]);
      // Render offscreen container dengan lebar A4 (210mm ≈ 794px @ 96dpi).
      host = document.createElement("div");
      host.style.cssText = "position:fixed;left:-99999px;top:0;width:794px;background:#fff;";
      host.innerHTML = built.html;
      document.body.appendChild(host);
      // Beri waktu image logo & fonts di-render.
      await new Promise((r) => setTimeout(r, 400));
      const sections = host.querySelectorAll(".inv-page");
      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const A4_W = 210; const A4_H = 297;
      for (let i = 0; i < sections.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(sections[i], {
          scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false,
        });
        const img = canvas.toDataURL("image/jpeg", 0.92);
        if (i > 0) pdf.addPage();
        // Fit ke A4 width, height proporsional.
        // Okt 2026 — skala proporsional agar selalu muat 1 halaman A4 (tanpa distorsi/terpotong).
        let w = A4_W - 30; // margin 15mm × 2
        let h = (canvas.height * w) / canvas.width;
        if (h > A4_H - 30) { w = (w * (A4_H - 30)) / h; h = A4_H - 30; }
        pdf.addImage(img, "JPEG", 15 + (A4_W - 30 - w) / 2, 15, w, h);
      }
      // Naming: single invoice pakai id-nya, bulk pakai count + timestamp.
      const nums = built.numbers;
      const fname = nums.length === 1
        ? `Invoice_AROMANET_${nums[0]}.pdf`
        : `Invoice_AROMANET_${nums.length}_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.pdf`;
      pdf.save(fname);
      toast.success(nums.length === 1
        ? `Invoice ${nums[0]} tersimpan sebagai PDF`
        : `${nums.length} invoice tersimpan sebagai PDF`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal menyimpan PDF");
    } finally {
      if (host && host.parentNode) host.parentNode.removeChild(host);
    }
  };

  const bulkSavePdf = async () => {
    if (savingPdf || pdfRowId || printCount === 0) return;
    setSavingPdf(true);
    try {
      await runSavePdf(null);
    } finally {
      setSavingPdf(false);
    }
  };

  const saveNbRowPdf = async (nb) => {
    if (savingPdf || pdfRowId) return;
    setPdfRowId(nb.id);
    try { await runSavePdf([], [nb.id]); } finally { setPdfRowId(null); }
  };

  const saveRowPdf = async (invoiceId) => {
    if (savingPdf || pdfRowId) return;
    setPdfRowId(invoiceId);
    try {
      await runSavePdf([invoiceId]);
    } finally {
      setPdfRowId(null);
    }
  };

  // ---- Sep 2026 Phase 22v — Kwitansi Pembayaran (browser view PDF) ----
  // Read-only: klik pada baris invoice LUNAS → buka PDF kwitansi di
  // tab baru. TIDAK memicu download otomatis. Popup wajib diperbolehkan
  // browser (window.open dipanggil di dalam handler klik langsung).
  const [receiptRowId, setReceiptRowId] = useState(null);

  // Terbilang (integer → kata Indonesia). Handle sampai triliun.
  const terbilang = (n) => {
    const num = Math.floor(Math.abs(Number(n) || 0));
    if (num === 0) return "Nol";
    const satuan = ["", "Satu", "Dua", "Tiga", "Empat", "Lima", "Enam", "Tujuh", "Delapan", "Sembilan", "Sepuluh", "Sebelas"];
    const rec = (v) => {
      if (v < 12) return satuan[v];
      if (v < 20) return rec(v - 10) + " Belas";
      if (v < 100) return rec(Math.floor(v / 10)) + " Puluh" + (v % 10 ? " " + rec(v % 10) : "");
      if (v < 200) return "Seratus" + (v - 100 ? " " + rec(v - 100) : "");
      if (v < 1000) return rec(Math.floor(v / 100)) + " Ratus" + (v % 100 ? " " + rec(v % 100) : "");
      if (v < 2000) return "Seribu" + (v - 1000 ? " " + rec(v - 1000) : "");
      if (v < 1000000) return rec(Math.floor(v / 1000)) + " Ribu" + (v % 1000 ? " " + rec(v % 1000) : "");
      if (v < 1000000000) return rec(Math.floor(v / 1000000)) + " Juta" + (v % 1000000 ? " " + rec(v % 1000000) : "");
      if (v < 1000000000000) return rec(Math.floor(v / 1000000000)) + " Milyar" + (v % 1000000000 ? " " + rec(v % 1000000000) : "");
      return rec(Math.floor(v / 1000000000000)) + " Triliun" + (v % 1000000000000 ? " " + rec(v % 1000000000000) : "");
    };
    return rec(num);
  };

  // Format tanggal ISO (YYYY-MM-DD) → "01 September 2026".
  const formatTanggalId = (iso) => {
    if (!iso) return "-";
    const [y, m, d] = String(iso).split("T")[0].split("-");
    if (!y || !m || !d) return String(iso);
    const bulan = ["", "Januari", "Februari", "Maret", "April", "Mei", "Juni",
                   "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
    return `${parseInt(d, 10).toString().padStart(2, "0")} ${bulan[parseInt(m, 10)] || m} ${y}`;
  };

  const buildReceiptHtml = ({ payment, invoice, customer, branding, printerRole, printerName }) => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const nl2br = (s) => esc(s).replace(/\n/g, "<br/>");
    const rupiah = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
    const cleanPackageName = (name) => {
      const raw = String(name || "").trim();
      return raw.replace(/\s+\d+(?:[.,]\d+)?\s*Mbps\s*$/i, "").trim() || raw;
    };
    const companyName = branding.company_name || "AROMA NET";
    const kwitansiNo = `KW-${String(payment.payment_id || "").replace(/^PAY-/, "")}`;
    // Ekstrak kota dari alamat perusahaan (heuristik: pola "Kab. XXX").
    const cityMatch = /Kab\.\s*([^\n,]+)/i.exec(branding.company_address || "");
    const city = cityMatch ? cityMatch[1].trim() : "";
    const today = formatTanggalId(new Date().toISOString().slice(0, 10));
    const rightInfo = [
      branding.company_address ? `<tr><td class="lbl">Alamat</td><td class="sep">:</td><td>${nl2br(branding.company_address)}</td></tr>` : "",
      branding.company_npwp ? `<tr><td class="lbl">NPWP</td><td class="sep">:</td><td>${esc(branding.company_npwp)}</td></tr>` : "",
      branding.company_phone ? `<tr><td class="lbl">Telp / WhatsApp</td><td class="sep">:</td><td>${esc(branding.company_phone)}</td></tr>` : "",
    ].filter(Boolean).join("");
    const bankRow = payment.method === "TRANSFER"
      ? `<tr><td class="lbl">Bank</td><td class="sep">:</td><td>Transfer</td></tr>`
      : "";
    const refRow = (payment.notes || "").trim()
      ? `<tr><td class="lbl">Referensi</td><td class="sep">:</td><td>${esc(payment.notes)}</td></tr>`
      : "";
    const paket = cleanPackageName(invoice.package_name_snapshot);
    const amount = Number(payment.amount_received || 0);
    const style = RECEIPT_PRINT_STYLE;
    return `<html><head><title>Kwitansi ${esc(kwitansiNo)}</title><style>${style}</style></head><body>
      <section class="rcpt-page">
        <header class="rcpt-hdr">
          <div class="hdr-left">
            ${branding.logo_data_url ? `<div class="logo-wrap"><img src="${esc(branding.logo_data_url)}" alt="Logo" class="rcpt-logo" /></div>` : ""}
            ${branding.slogan ? `<div class="rcpt-slogan">${esc(branding.slogan)}</div>` : ""}
          </div>
          <div class="hdr-right">
            <div class="rcpt-brand">${esc(companyName)}</div>
            <table class="hdr-info">${rightInfo}</table>
          </div>
        </header>
        <hr class="divider-green"/>
        <div class="rcpt-title-wrap">
          <div class="rcpt-title">KWITANSI PEMBAYARAN</div>
          <span class="lunas-text">LUNAS</span>
        </div>
        <div class="rcpt-no">No. Kwitansi: <span class="mono">${esc(kwitansiNo)}</span></div>
        <div class="rcpt-body">
          <div class="col">
            <div class="section-title">Diterima Dari</div>
            <table class="kv">
              <tr><td class="lbl">ID Pelanggan</td><td class="sep">:</td><td class="mono">${esc(invoice.customer_id)}</td></tr>
              <tr><td class="lbl">Nama Pelanggan</td><td class="sep">:</td><td>${esc(invoice.customer_name_snapshot)}</td></tr>
              <tr><td class="lbl">Alamat</td><td class="sep">:</td><td>${customer.alamat ? nl2br(customer.alamat) : '<span style="color:#9CA3AF">—</span>'}</td></tr>
              <tr><td class="lbl">No. HP</td><td class="sep">:</td><td>${customer.no_hp ? esc(customer.no_hp) : '<span style="color:#9CA3AF">—</span>'}</td></tr>
            </table>
          </div>
          <div class="col">
            <div class="section-title">Informasi Pembayaran</div>
            <table class="kv">
              <tr><td class="lbl">No. Pembayaran</td><td class="sep">:</td><td class="mono">${esc(payment.payment_id)}</td></tr>
              <tr><td class="lbl">Tanggal Bayar</td><td class="sep">:</td><td>${esc(formatTanggalId(payment.payment_date))}</td></tr>
              <tr><td class="lbl">Metode Pembayaran</td><td class="sep">:</td><td>${esc(payment.method || "-")}</td></tr>
              ${bankRow}
              ${refRow}
            </table>
          </div>
        </div>
        <div class="rcpt-detail-title">Rincian Pembayaran</div>
        <table class="tbl">
          <thead>
            <tr>
              <th>No. Invoice</th>
              <th>Periode</th>
              <th>Paket</th>
              <th>Tagihan</th>
              <th>Sudah Dibayar</th>
              <th>Sisa Tagihan</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="mono">${esc(invoice.invoice_id)}</td>
              <td>${esc(invoice.periode_tagihan)}</td>
              <td>${esc(paket)}</td>
              <td class="r">${rupiah(invoice.total_harus_dibayar)}</td>
              <td class="r">${rupiah(invoice.total_dibayar)}</td>
              <td class="r">${rupiah(invoice.sisa_tagihan)}</td>
            </tr>
          </tbody>
        </table>
        <div class="total-row"><div>Total Dibayar</div><div class="val">${rupiah(amount)}</div></div>
        <div class="terbilang">
          <div class="lbl">Terbilang</div><div>:</div>
          <div><em>${esc(terbilang(amount))} Rupiah</em></div>
        </div>
        <div class="keterangan">Demikian kwitansi ini dibuat sebagai bukti pembayaran yang sah.</div>
        <div class="sign-row">
          <div class="sign-left">${city ? esc(city) + ", " : ""}${esc(today)}</div>
          <div class="sign-right">
            <div>Hormat kami,</div>
            <div class="role">Dicetak oleh: ${esc(printerRole)}</div>
            <div class="line"></div>
            <div class="name">( ${esc(printerName)} )</div>
          </div>
        </div>
        <div class="footer-note">Kwitansi ini dicetak secara otomatis oleh sistem dan sah tanpa tanda tangan basah.</div>
      </section>
    </body></html>`;
  };

  const openNbRowReceipt = async (nb) => {
    if (receiptRowId) return;
    setReceiptRowId(nb.id);
    try {
      const err = await openNbReceipt(() => api.get(`/billing/nb-invoices/${nb.id}`).then((r) => r.data),
        api.get("/settings/invoice-branding").then((r) => r.data || {}).catch(() => ({})), user);
      if (err) toast.error(formatApiErrorDetail(err) || err);
    } finally { setReceiptRowId(null); }
  };

  // Buka Kwitansi Pembayaran di tab baru sebagai PDF (bukan download).
  const openReceipt = async (invoiceId) => {
    if (receiptRowId) return;
    // Buka tab DULU (dalam handler klik langsung) supaya lolos popup blocker.
    const newWin = window.open("", "_blank");
    if (!newWin) {
      toast.error("Popup diblokir. Izinkan popup pada browser untuk melihat kwitansi.");
      return;
    }
    try {
      newWin.document.open();
      newWin.document.write("<!doctype html><html><head><title>Memuat kwitansi…</title><meta charset='utf-8'></head><body style='font-family:system-ui,sans-serif;padding:24px;color:#374151'>Memuat kwitansi pembayaran…</body></html>");
      newWin.document.close();
    } catch {}
    setReceiptRowId(invoiceId);
    let host = null;
    try {
      const { data: paymentsResp } = await api.get("/billing/payments", {
        params: { invoice_id: invoiceId, status: "CONFIRMED", limit: 200 },
      });
      const list = (paymentsResp?.items || []).filter((p) => p.status === "CONFIRMED");
      if (list.length === 0) {
        newWin.close();
        toast.error("Invoice ini belum memiliki pembayaran valid untuk dibuatkan kwitansi.");
        return;
      }
      const latest = list[0]; // sorted DESC by payment_id
      const [invResp, custResp, brandingResp] = await Promise.all([
        api.get(`/billing/invoices/${invoiceId}`),
        api.get(`/billing/customers/${latest.customer_id}`).catch(() => ({ data: {} })),
        api.get("/settings/invoice-branding").catch(() => ({ data: {} })),
      ]);
      const roleLabel = ({ admin: "Admin", koord: "Koordinator", kolektor: "Kolektor", teknisi: "Teknisi", spv: "Supervisor" })[user?.role] || (user?.role || "-");
      const printerName = (user?.name || "").trim() || (user?.username || "-");
      const html = buildReceiptHtml({
        payment: latest,
        invoice: invResp.data,
        customer: custResp.data || {},
        branding: brandingResp.data || {},
        printerRole: roleLabel,
        printerName,
      });
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import("jspdf"),
        import("html2canvas"),
      ]);
      host = document.createElement("div");
      host.style.cssText = "position:fixed;left:-99999px;top:0;width:794px;background:#fff;";
      host.innerHTML = html;
      document.body.appendChild(host);
      await new Promise((r) => setTimeout(r, 400));
      const section = host.querySelector(".rcpt-page");
      // Scale 2.5 + JPEG 0.95 memberi ketajaman yang jauh lebih baik
      // (font tidak blur di PDF viewer) tanpa membengkakkan ukuran
      // file seperti PNG scale 3.
      const canvas = await html2canvas(section, { scale: 2.5, backgroundColor: "#ffffff", useCORS: true, logging: false });
      const img = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const w = 210 - 30;
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(img, "JPEG", 15, 15, w, Math.min(h, 297 - 30));
      const blob = pdf.output("blob");
      const url = URL.createObjectURL(blob);
      // Embed PDF sebagai <embed> di tab yang sudah dibuka. Kompatibilitas
      // lebih baik daripada location.href=blob: pada beberapa Chromium
      // build (tab tetap "about:blank" tanpa error yang jelas).
      try {
        newWin.document.open();
        newWin.document.write(
          `<!doctype html><html><head><title>Kwitansi Pembayaran</title></head>` +
          `<body style="margin:0;height:100vh"><embed src="${url}" type="application/pdf" style="width:100%;height:100%"/></body></html>`
        );
        newWin.document.close();
      } catch {
        newWin.location.href = url;
      }
    } catch (err) {
      try { newWin.close(); } catch {}
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal menampilkan kwitansi");
    } finally {
      if (host && host.parentNode) host.parentNode.removeChild(host);
      setReceiptRowId(null);
    }
  };

  // Sep 2026 Phase 23a — Bulk Bayar handler. Loop sequential (bukan
  // parallel) supaya order predictable + tidak race pada logic
  // received_by kolektor assignment. Setiap invoice pakai `pay_full`
  // → backend recalc sisa_tagihan aktual = source of truth.
  const validForBulkPay = () => rows.filter(
    (r) => selected.has(r.invoice_id)
      && r.status !== "LUNAS"
      && r.status !== "DIBATALKAN"
      && Number(r.sisa_tagihan) > 0
      && !r.carry_forward_to
  );
  const bulkPayTotal = () => validForBulkPay().reduce(
    (sum, r) => sum + Number(r.sisa_tagihan || 0), 0
  );

  const runBulkPay = async () => {
    if (!isAdmin) return; // hard guard — UI harusnya sudah cegah, tapi jaga-jaga
    if (bulkPaying) return;
    const valid = validForBulkPay();
    if (valid.length === 0) {
      toast.error("Tidak ada invoice valid untuk dibayar (semua LUNAS/Dibatalkan)");
      return;
    }
    setBulkPaying(true);
    const successList = [];
    const failList = [];
    // Sequential loop (bukan Promise.all) supaya server tidak race
    // pada received_by lookup + admin bisa lihat progress bila diperlukan
    // via toast di masa depan.
    for (const inv of valid) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await api.post("/billing/payments", {
          invoice_id: inv.invoice_id,
          method: bulkPayForm.method,
          payment_date: bulkPayForm.payment_date,
          notes: bulkPayForm.notes || "Bulk pembayaran",
          pay_full: true,
        });
        successList.push(inv.invoice_id);
      } catch (err) {
        failList.push({
          invoice_id: inv.invoice_id,
          customer: inv.customer_name_snapshot || inv.customer_id,
          error: formatApiErrorDetail(err?.response?.data?.detail) || "Gagal",
        });
      }
    }
    setBulkPayResult({ success: successList, fail: failList });
    setBulkPaying(false);
    if (successList.length > 0) {
      toast.success(`${successList.length} invoice berhasil dibayar${failList.length ? `, ${failList.length} gagal` : ""}`);
    } else {
      toast.error(`Bulk bayar gagal untuk ${failList.length} invoice`);
    }
    // Refresh list + clear selection hanya untuk yang berhasil
    if (successList.length > 0) {
      setSelected(new Set());
      await load();
    }
  };


  const someSelected = printCount > 0 && !allSelected;

  const runBulkDelete = async () => {
    if (selectionCount === 0) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.delete("/billing/invoices/bulk", {
        data: { invoice_ids: Array.from(selected) },
      });
      const del = data?.deleted?.length || 0;
      const skip = data?.skipped?.length || 0;
      const nf = data?.not_found?.length || 0;
      if (del > 0) toast.success(`${del} invoice terhapus`);
      if (skip > 0) {
        const first = data.skipped[0];
        toast.error(`${skip} tidak dapat dihapus: ${first.reason}`);
      }
      if (nf > 0) toast.error(`${nf} invoice tidak ditemukan`);
      setShowBulkConfirm(false);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal menghapus invoice");
    } finally {
      setBulkDeleting(false);
    }
  };

  const loadNb = async (seq) => {
    // Hanya Admin; disembunyikan bila filter khusus bulanan aktif (aging/kolektor).
    if (!isAdmin || aging || collectorId || jenis === "BULANAN") { setNbRows([]); setNbTotal(0); return; }
    const params = { limit: Math.min(pageSize, 100), page };
    if (periode) params.periode = periode;
    if (status) params.status = status;
    if (customerId) params.q = customerId;
    if (wilayahId) params.wilayah_id = wilayahId;
    if (customerStatus) params.customer_status_layanan = customerStatus;
    try {
      const { data } = await api.get("/billing/nb-invoices", { params });
      if (seq !== reqSeq.current) return;
      setNbRows(data.items || []); setNbTotal(data.total || 0);
    } catch {
      if (seq === reqSeq.current) { setNbRows([]); setNbTotal(0); }
    }
  };

  const load = async () => {
    // Hanya respons request terbaru yang boleh memperbarui daftar invoice.
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const params = { limit: pageSize, page };
      if (periode) params.periode_tagihan = periode;
      if (status) params.status = status;
      if (customerId) params.customer_name_q = customerId;
      if (aging) { params.aging = aging; if (agingAsOf) params.aging_as_of = agingAsOf; }
      if (collectorId && isAdmin) params.collector_user_id = collectorId;
      if (wilayahId && isAdmin) params.wilayah_id = wilayahId;
      if (customerStatus && isAdmin) params.customer_status_layanan = customerStatus;
      if (sortBy) { params.sort_by = sortBy; params.sort_dir = sortDir; }
      if (includeCarried) params.include_carried = true;
      loadNb(seq);
      if (isAdmin && jenis === "NB") { setRows([]); setTotal(0); setAgingSum(null); setAgingRef(""); return; }
      const { data } = await api.get("/billing/invoices", { params });
      if (seq !== reqSeq.current) return;
      setRows(data.items || []);
      setTotal(data.total || 0);
      setAgingSum(aging ? (data.sum_aging_amount ?? null) : null);
      setAgingRef(aging ? (data.aging_as_of || "") : "");
    } catch (e) {
      if (seq === reqSeq.current) toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal memuat invoice");
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [periode, status, page, pageSize, includeCarried, customerIdDeb, collectorId, wilayahId, customerStatus, sortBy, sortDir, aging, jenis]);
  useEffect(() => { setPage(1); }, [periode, status, customerId, includeCarried, collectorId, wilayahId, jenis]);
  // Reset selection saat filter berubah supaya user tidak salah hapus invoice
  // yang tidak lagi tampil di viewport-nya.
  useEffect(() => { clearSelection(); /* eslint-disable-next-line */ }, [periode, status, customerId, page, collectorId, wilayahId, jenis]);

  // Feb 2026 — ADMIN ONLY: prefetch daftar user kolektor untuk dropdown
  // Filter Kolektor. Kolektor role tidak fetch endpoint ini (403).
  useEffect(() => {
    if (!isAdmin) return;
    let ok = true;
    (async () => {
      try {
        const [uRes, wRes] = await Promise.all([
          api.get("/users"),
          api.get("/billing/wilayah").catch(() => ({ data: { items: [] } })),
        ]);
        if (!ok) return;
        const list = (uRes.data || []).filter((u) => {
          const primary = (u.role || "").toLowerCase();
          const extra = Array.isArray(u.extra_roles) ? u.extra_roles.map((x) => (x || "").toLowerCase()) : [];
          return primary === "kolektor" || extra.includes("kolektor");
        }).map((u) => ({ id: u.id, name: u.name || u.username, username: u.username }));
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setCollectorList(list);
        const wl = (wRes.data?.items || []).map((w) => ({ wilayah_id: w.wilayah_id, nama_wilayah: w.nama_wilayah }));
        wl.sort((a, b) => (a.nama_wilayah || "").localeCompare(b.nama_wilayah || ""));
        setWilayahList(wl);
      } catch {
        /* silent — dropdown akan kosong bila gagal */
      }
    })();
    return () => { ok = false; };
  }, [isAdmin]);

  // Feb 2026 — Sortable columns Wilayah/Kolektor. Server-side: passthrough
  // sort_by/sort_dir ke backend supaya urutan konsisten lintas halaman.
  // Klik header 1x = asc, 2x = desc, 3x = reset (default DB sort).
  const cycleSort = (key) => {
    if (sortBy !== key) { setSortBy(key); setSortDir("asc"); setPage(1); return; }
    if (sortDir === "asc") { setSortDir("desc"); setPage(1); return; }
    setSortBy(""); setSortDir("asc"); setPage(1);
  };
  const displayRows = rows;
  const sortIndicator = (key) => (sortBy === key ? (sortDir === "asc" ? " ↑" : " ↓") : "");

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="billing-invoices-page">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Invoice</h1>
          <p className="text-sm text-[#6B7280]" data-testid="billing-inv-summary">
            Total: {total + nbTotal}
            {nbTotal > 0 && <span data-testid="billing-inv-summary-split"> ({total} bulanan · {nbTotal} barang/jasa)</span>}
            {isAdmin && collectorId && (
              <>
                {" · "}
                <span data-testid="billing-inv-collector-active-badge">
                  Kolektor: <b className="text-[#111827]">
                    {collectorId === "KOLEKTOR-SYSTEM"
                      ? "Kolektor Sistem"
                      : (collectorList.find((c) => c.id === collectorId) || {}).name || collectorId}
                  </b>
                </span>
              </>
            )}
            {isAdmin && wilayahId && (
              <>
                {" · "}
                <span data-testid="billing-inv-wilayah-active-badge">
                  Wilayah: <b className="text-[#111827]">
                    {(wilayahList.find((w) => w.wilayah_id === wilayahId) || {}).nama_wilayah || wilayahId}
                  </b>
                </span>
              </>
            )}
            {isAdmin && customerStatus && (
              <>
                {" · "}
                <span data-testid="billing-inv-customer-status-active-badge">
                  Status Layanan: <b className="text-[#111827]">{customerStatus}</b>
                </span>
              </>
            )}
          </p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            {isAdmin && selectionCount > 0 && (
              <Button
                onClick={() => setShowBulkConfirm(true)}
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                data-testid="billing-inv-bulk-delete-btn"
                title="Hapus invoice yang dipilih" aria-label="Hapus invoice yang dipilih">
                <Trash2 className="w-4 h-4 mr-1" /> Hapus Terpilih ({selectionCount})
              </Button>
            )}
            <Button
              onClick={bulkPrint}
              disabled={printCount === 0 || printing || savingPdf || !!pdfRowId}
              variant="outline"
              data-testid="billing-inv-bulk-print-btn"
              title={printCount === 0 ? "Pilih invoice terlebih dahulu" : "Cetak invoice yang dipilih"}
              aria-label="Cetak invoice yang dipilih">
              <Printer className="w-4 h-4 mr-1" />
              {printing ? "Menyiapkan…" : `Print Terpilih (${printCount})`}
            </Button>
            <Button
              onClick={bulkSavePdf}
              disabled={printCount === 0 || printing || savingPdf || !!pdfRowId}
              variant="outline"
              data-testid="billing-inv-bulk-pdf-btn"
              title={printCount === 0 ? "Pilih invoice terlebih dahulu" : "Simpan invoice sebagai PDF"}
              aria-label="Simpan invoice sebagai PDF">
              <FileDown className="w-4 h-4 mr-1" />
              {savingPdf ? "Menyimpan…" : `Simpan sebagai PDF (${printCount})`}
            </Button>
            {isAdmin && (
              <Button
                onClick={() => { setBulkPayResult(null); setShowBulkPay(true); }}
                disabled={selectionCount === 0 || bulkPaying}
                data-testid="billing-inv-bulk-pay-btn"
                title={selectionCount === 0 ? "Pilih invoice terlebih dahulu" : "Catat pembayaran untuk invoice yang dipilih (Admin only)"}
                aria-label="Bayar invoice yang dipilih">
                <Wallet className="w-4 h-4 mr-1" />
                {bulkPaying ? "Memproses…" : `Bayar Terpilih (${selectionCount})`}
              </Button>
            )}
            {isAdmin && (
              <Button
                onClick={() => setShowBulkVoidMulti(true)}
                disabled={selectionCount === 0}
                variant="outline"
                className="border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2]"
                data-testid="billing-inv-bulk-void-multi-btn"
                title={selectionCount === 0 ? "Pilih invoice terlebih dahulu" : "Void pembayaran dari invoice yang dipilih (Admin only)"}
                aria-label="Void pembayaran invoice yang dipilih">
                <XCircle className="w-4 h-4 mr-1" />
                {`Void Pembayaran Terpilih (${selectionCount})`}
              </Button>
            )}
            {isAdmin && nbSel.size > 0 && (
              <Button variant="outline" onClick={() => setNbBulk("void")} data-testid="billing-inv-nb-bulk-void-btn"
                      className="border-[#B45309] text-[#B45309] hover:bg-[#FFFBEB]" title="Void dokumen Invoice Barang/Jasa terpilih (bukan void pembayaran)">
                <Ban className="w-4 h-4 mr-1" /> Void Invoice Terpilih ({nbSel.size})
              </Button>
            )}
            {isAdmin && nbSel.size > 0 && (
              <Button onClick={() => setNbBulk("delete")} data-testid="billing-inv-nb-bulk-delete-btn"
                      className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" title="Hapus permanen Invoice Barang/Jasa terpilih">
                <Trash2 className="w-4 h-4 mr-1" /> Hapus Barang/Jasa Terpilih ({nbSel.size})
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" onClick={() => setShowAutoInv(true)} data-testid="billing-inv-autoinv-btn"
                      className="border-[#0D9488] text-[#0F766E] hover:bg-[#F0FDFA]">
                <CalendarClock className="w-4 h-4 mr-1" /> Invoice Otomatis
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" onClick={() => setShowNb(true)} data-testid="billing-inv-nb-btn"
                      className="border-[#0055FF] text-[#0055FF] hover:bg-[#EFF6FF]">
                <ReceiptText className="w-4 h-4 mr-1" /> Invoice Barang/Jasa
              </Button>
            )}
            <Button onClick={() => setShowGenerate(true)} data-testid="billing-inv-generate-btn">
              <Plus className="w-4 h-4 mr-1" /> Generate Invoice
            </Button>
          </div>
        )}
      </header>
      {nbBulk && <NbBulkActionDialog mode={nbBulk} items={nbRows.filter((r) => nbSel.has(r.id))}
                                     onClose={() => setNbBulk(null)} onDone={() => { setNbSel(new Set()); load(); }} />}
      {(showNb || nbOpenId) && <NbInvoiceDialog initialId={nbOpenId} onClose={() => { setShowNb(false); setNbOpenId(null); load(); }} />}
      {showAutoInv && <AutoInvoiceDialog isAdmin={isAdmin} onClose={() => setShowAutoInv(false)} onDone={load} />}

      {aging && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-xs text-[#991B1B]"
             data-testid="billing-inv-aging-banner">
          <span className="font-semibold">Filter Aging: {AGING_LABEL[aging]}</span>
          <span>Posisi per {agingRef || agingAsOf || "—"} (WIB) · Sebagian/Belum Lunas, sisa &gt; 0 · bon carry-forward memakai jatuh tempo asal</span>
          <span>· <b className="font-mono">{total}</b> invoice · Σ tunggakan <b className="font-mono" data-testid="billing-inv-aging-sum">{agingSum == null ? "…" : formatRupiah(agingSum)}</b></span>
          <button type="button" onClick={() => { setAging(""); setPage(1); const sp = new URLSearchParams(searchParams); sp.delete("aging"); sp.delete("aging_as_of"); setSearchParams(sp, { replace: true }); }}
                  className="ml-auto inline-flex items-center gap-1 font-semibold hover:underline"
                  data-testid="billing-inv-aging-clear">
            <X className="w-3.5 h-3.5" /> Hapus filter aging
          </button>
        </div>
      )}
      <div className="space-y-2" data-testid="billing-inv-filters">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex gap-2 shrink-0">
          <Input type="month" value={periode} onChange={(e) => setPeriode(e.target.value)}
                 className="h-9 w-full sm:w-[180px]" data-testid="billing-inv-periode-filter" />
          <Button type="button" variant="outline" size="sm" className="h-9 shrink-0"
                  disabled={periode === currentWibPeriode()} onClick={() => setPeriode(currentWibPeriode())}
                  title="Kembali ke bulan berjalan (WIB)" data-testid="billing-inv-periode-this-month">
            <CalendarClock className="h-4 w-4 mr-1" />Bulan Ini
          </Button>
        </div>
        <div className="relative w-full sm:max-w-[420px]">
          <Input placeholder="Cari nama customer atau ID invoice..." value={customerId}
                 onChange={(e) => setCustomerId(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 className={customerId ? "h-9 w-full pr-16" : "h-9 w-full"}
                 data-testid="billing-inv-search" />
          {customerId && (loading || customerId !== customerIdDeb) && (
            <Loader2 className="absolute right-9 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-[#6B7280]"
                     aria-label="Mencari…" data-testid="billing-inv-search-loading" />
          )}
          {customerId && (
            <button type="button" onClick={() => setCustomerId("")}
                    aria-label="Bersihkan pencarian"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#6B7280] hover:text-[#111827] hover:bg-[#F3F4F6]"
                    data-testid="billing-inv-search-clear">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap xl:grid xl:grid-cols-4 2xl:flex 2xl:flex-nowrap lg:items-center gap-2" data-testid="billing-inv-filter-row">
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="h-9 border rounded-md px-3 text-sm bg-white w-full lg:w-[190px] xl:w-full 2xl:w-auto 2xl:flex-1 2xl:min-w-0 truncate"
                data-testid="billing-inv-status-filter">
          <option value="">Semua Status</option>
          <option value="BELUM_LUNAS">Belum Lunas</option>
          <option value="SEBAGIAN">Dibayar Sebagian</option>
          <option value="LUNAS">Lunas</option>
          <option value="DIBATALKAN">Dibatalkan</option>
          <option value="SEBAGIAN,BELUM_LUNAS">Aktif bersisa (Sebagian + Belum Lunas)</option>
          <option value="LUNAS,SEBAGIAN,BELUM_LUNAS">Semua kecuali Dibatalkan</option>
        </select>
        {isAdmin && (
          <select value={jenis} onChange={(e) => setJenis(e.target.value)}
                  className="h-9 border rounded-md px-3 text-sm bg-white w-full lg:w-[190px] xl:w-full 2xl:w-auto 2xl:flex-1 2xl:min-w-0 truncate"
                  data-testid="billing-inv-jenis-filter" aria-label="Jenis Invoice">
            <option value="">Semua Jenis Invoice</option>
            <option value="BULANAN">Bulanan</option>
            <option value="NB">Barang/Jasa</option>
          </select>
        )}
        {isAdmin && (
          <select value={collectorId} onChange={(e) => setCollectorId(e.target.value)}
                  className="h-9 border rounded-md px-3 text-sm bg-white w-full lg:w-[190px] xl:w-full 2xl:w-auto 2xl:flex-1 2xl:min-w-0 truncate"
                  data-testid="billing-inv-collector-filter">
            <option value="">Semua Kolektor</option>
            <option value="KOLEKTOR-SYSTEM">Kolektor Sistem</option>
            {collectorList.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
        {isAdmin && (
          <select value={wilayahId} onChange={(e) => setWilayahId(e.target.value)}
                  className="h-9 border rounded-md px-3 text-sm bg-white w-full lg:w-[190px] xl:w-full 2xl:w-auto 2xl:flex-1 2xl:min-w-0 truncate"
                  data-testid="billing-inv-wilayah-filter">
            <option value="">Semua Wilayah</option>
            {wilayahList.map((w) => (
              <option key={w.wilayah_id} value={w.wilayah_id}>{w.nama_wilayah}</option>
            ))}
          </select>
        )}
        {isAdmin && (
          <select value={customerStatus} onChange={(e) => setCustomerStatus(e.target.value)}
                  className="h-9 border rounded-md px-3 text-sm bg-white w-full lg:w-[190px] xl:w-full 2xl:w-auto 2xl:flex-1 2xl:min-w-0 truncate"
                  data-testid="billing-inv-customer-status-filter">
            <option value="">Semua Status Layanan</option>
            <option value="AKTIF">Aktif</option>
            <option value="ISOLIR">Isolir</option>
            <option value="BERHENTI">Berhenti</option>
            <option value="FREE">Free</option>
          </select>
        )}
        <Button variant="outline" onClick={load} className="h-9 w-full lg:w-auto xl:w-full 2xl:w-auto px-5 shrink-0" data-testid="billing-inv-apply">Terapkan</Button>
        <label className="h-9 flex items-center gap-2 text-xs cursor-pointer px-3 border rounded-md bg-white select-none whitespace-nowrap shrink-0 sm:col-span-2 lg:col-span-1"
                data-testid="billing-inv-include-carried-wrap">
          <input
            type="checkbox"
            checked={includeCarried}
            onChange={(e) => setIncludeCarried(e.target.checked)}
            className="h-4 w-4 cursor-pointer"
            data-testid="billing-inv-include-carried"
          />
          <span>Tampilkan Invoice Terkonsolidasi</span>
        </label>
      </div>
      </div>

      {/* Feb 2026 — Pagination dipindah ke panel atas supaya user tidak perlu scroll. */}
      <div className="flex items-center justify-between text-sm py-2 flex-wrap gap-2" data-testid="billing-inv-pagination">
        <div>
          {Math.max(total, nbTotal) > pageSize
            ? <>Halaman {page} dari {Math.ceil(Math.max(total, nbTotal) / pageSize)} · Total {total + nbTotal} invoice</>
            : <>Total {total + nbTotal} invoice</>}
          {nbTotal > 0 && <span className="text-xs text-[#6B7280]" data-testid="billing-inv-pagination-split"> ({total} bulanan · {nbTotal} barang/jasa)</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1 text-xs text-[#6B7280]">
            Per halaman:
            <select value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                    className="border rounded-md px-2 py-1 text-xs bg-white"
                    data-testid="billing-inv-page-size">
              {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Sebelumnya</Button>
          <Button size="sm" variant="outline" onClick={() => setPage((p) => p + 1)} disabled={page * pageSize >= Math.max(total, nbTotal)}>Berikutnya</Button>
        </div>
      </div>

      {loading && <div className="text-sm text-[#6B7280]">Memuat…</div>}
      {!loading && rows.length === 0 && nbRows.length === 0 && (
        <div className="p-8 text-center text-sm text-[#6B7280] border rounded-md" data-testid="billing-inv-empty">
          Belum ada invoice.
        </div>
      )}

      {/* Desktop table (md+) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm border-collapse min-w-[720px]">
          <thead>
            <tr className="text-left border-b bg-[#F8F9FA]">
              {isAdmin && (
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 cursor-pointer"
                    data-testid="billing-inv-select-all"
                    title="Pilih semua invoice" aria-label="Pilih semua invoice"
                  />
                </th>
              )}
              <th className="px-3 py-2">Invoice</th>
              <th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2 hidden lg:table-cell cursor-pointer select-none hover:bg-[#EEF2FF]"
                  onClick={() => cycleSort("wilayah_id")}
                  data-testid="billing-inv-sort-wilayah">
                Wilayah{sortIndicator("wilayah_id")}
              </th>
              <th className="px-3 py-2 hidden lg:table-cell cursor-pointer select-none hover:bg-[#EEF2FF]"
                  onClick={() => cycleSort("current_collector_name")}
                  data-testid="billing-inv-sort-kolektor">
                Kolektor{sortIndicator("current_collector_name")}
              </th>
              <th className="px-3 py-2 hidden sm:table-cell">Periode</th>
              <th className="px-3 py-2 text-right">Tagihan</th>
              <th className="px-3 py-2 whitespace-nowrap" data-testid="billing-inv-due-head">Jatuh Tempo</th>
              <th className="px-3 py-2 text-right hidden md:table-cell">Dibayar</th>
              <th className="px-3 py-2 text-left hidden md:table-cell">Tanggal Pembayaran</th>
              <th className="px-3 py-2 text-right">Sisa</th>
              {aging && <th className="px-3 py-2 whitespace-nowrap" data-testid="billing-inv-overdue-head">Umur Tunggakan ↓</th>}
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 hidden md:table-cell">Penerima Uang</th>
              <th className="px-3 py-2 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((inv) => (
              <tr key={inv.invoice_id}
                  className={`border-b hover:bg-[#F8F9FA] ${selected.has(inv.invoice_id) ? "bg-[#FEF2F2]" : ""}`}
                  data-testid={`billing-inv-row-${inv.invoice_id}`}>
                {isAdmin && (
                  <td className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={selected.has(inv.invoice_id)}
                      onChange={() => toggleSelect(inv.invoice_id)}
                      className="h-4 w-4 cursor-pointer"
                      data-testid={`billing-inv-select-${inv.invoice_id}`}
                    />
                  </td>
                )}
                <td className="px-3 py-2 font-mono text-xs">{inv.invoice_id}</td>
                <td className="px-3 py-2">
                  <div className="font-semibold text-xs">{highlightMatch(inv.customer_name_snapshot || "", customerIdDeb)}</div>
                  <div className="text-xs text-[#6B7280] font-mono">{inv.customer_id}</div>
                </td>
                <td className="px-3 py-2 hidden lg:table-cell text-xs"
                    data-testid={`billing-inv-wilayah-${inv.invoice_id}`}>
                  {inv.wilayah_id
                    ? <span>{inv.wilayah_id}</span>
                    : <span className="text-[#9CA3AF]">—</span>}
                </td>
                <td className="px-3 py-2 hidden lg:table-cell text-xs"
                    data-testid={`billing-inv-kolektor-${inv.invoice_id}`}>
                  {inv.current_collector_name
                    ? <span className="truncate max-w-[180px] inline-block align-middle">{inv.current_collector_name}</span>
                    : <span className="text-[#9CA3AF]">—</span>}
                </td>
                <td className="px-3 py-2 hidden sm:table-cell text-xs">{periodeLabel(inv.periode_tagihan)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{formatRupiah(inv.total_harus_dibayar)}</td>
                <td className="px-3 py-2 text-xs"><DueDateCell inv={inv} /></td>
                <td className="px-3 py-2 text-right font-mono text-xs hidden md:table-cell">{formatRupiah(inv.total_dibayar)}</td>
                <td className="px-3 py-2 text-xs hidden md:table-cell" data-testid={`billing-inv-last-pay-${inv.invoice_id}`}>{formatPaymentDate(inv.last_payment_date)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{formatRupiah(inv.sisa_tagihan)}</td>
                {aging && <td className="px-3 py-2"><OverdueBadge inv={inv} /></td>}
                <td className="px-3 py-2"><Badge className={`text-xs ${STATUS_COLOR[inv.status] || ""}`}>{STATUS_LABEL[inv.status] || inv.status}</Badge></td>
                <td className="px-3 py-2 hidden md:table-cell text-xs"
                    data-testid={`billing-inv-receivers-${inv.invoice_id}`}>
                  {!inv.payment_receivers || inv.payment_receivers.length === 0 ? (
                    <span className="text-[#9CA3AF]">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1 max-w-[220px]">
                      {inv.payment_receivers.map((r, idx) => {
                        const label = receivedLabel({
                          method: r.method,
                          received_role: r.received_role,
                          received_by_name_snapshot: r.received_by_name_snapshot,
                        }).label;
                        const cls = r.method === "TUNAI"
                          ? "bg-[#D1FAE5] text-[#065F46]"
                          : r.method === "KANTOR"
                            ? "bg-[#DBEAFE] text-[#1E3A8A]"
                            : "bg-[#FEF3C7] text-[#92400E]";
                        return (
                          <span key={idx}
                                className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold truncate max-w-full ${cls}`}
                                title={label}>
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => setDetail(inv)}
                            data-testid={`billing-inv-detail-${inv.invoice_id}`}
                            title="Lihat Invoice" aria-label="Lihat Invoice">
                      <FileText className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => saveRowPdf(inv.invoice_id)}
                            disabled={savingPdf || (pdfRowId && pdfRowId !== inv.invoice_id) || pdfRowId === inv.invoice_id}
                            data-testid={`billing-inv-pdf-${inv.invoice_id}`}
                            title="Simpan invoice sebagai PDF" aria-label="Simpan invoice sebagai PDF">
                      <FileDown className="w-3 h-3" />
                      {pdfRowId === inv.invoice_id && <span className="ml-1 text-[10px]">…</span>}
                    </Button>
                    {inv.status === "LUNAS" && Number(inv.sisa_tagihan) === 0 && (
                      <Button size="sm" variant="outline"
                              onClick={() => openReceipt(inv.invoice_id)}
                              disabled={!!receiptRowId}
                              data-testid={`billing-inv-receipt-${inv.invoice_id}`}
                              title="Lihat kwitansi pembayaran" aria-label="Lihat kwitansi pembayaran">
                        <ReceiptText className="w-3 h-3" />
                        {receiptRowId === inv.invoice_id && <span className="ml-1 text-[10px]">…</span>}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            <NbInvoiceTableRows items={nbRows} isAdmin={isAdmin} onOpen={setNbOpenId} hl={(t) => highlightMatch(t, customerIdDeb)}
                                sel={nbSel} onToggle={toggleNbSelect} pdfId={pdfRowId} pdfBusy={savingPdf} onPdf={saveNbRowPdf}
                                rcptId={receiptRowId} onReceipt={openNbRowReceipt} />
          </tbody>
        </table>
      </div>

      {/* Mobile card list (<md) — Aug 2026 responsive rework. Data
          identik dengan tabel desktop; disajikan sebagai card vertikal
          agar tidak ada horizontal overflow di HP. */}
      <div className="md:hidden space-y-2" data-testid="billing-inv-mobile-list">
        {displayRows.map((inv) => (
          <div key={inv.invoice_id}
                className={`border rounded-md bg-white p-3 space-y-2 ${selected.has(inv.invoice_id) ? "border-[#DC2626] bg-[#FEF2F2]" : ""}`}
                data-testid={`billing-inv-mobile-${inv.invoice_id}`}>
            <div className="flex items-start justify-between gap-2">
              {isAdmin && (
                <label className="flex-shrink-0 pt-0.5 cursor-pointer h-6 w-6 flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={selected.has(inv.invoice_id)}
                    onChange={() => toggleSelect(inv.invoice_id)}
                    className="h-5 w-5 cursor-pointer"
                    data-testid={`billing-inv-mobile-select-${inv.invoice_id}`}
                  />
                </label>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{inv.customer_name_snapshot}</div>
                <div className="text-[11px] text-[#6B7280] font-mono">{inv.customer_id}</div>
                <div className="text-[11px] text-[#6B7280] font-mono mt-0.5">{inv.invoice_id}</div>
              </div>
              <Badge className={`text-[10px] flex-shrink-0 ${STATUS_COLOR[inv.status] || ""}`}>
                {STATUS_LABEL[inv.status] || inv.status}
              </Badge>
            </div>
            <div className="text-xs space-y-1 pt-1 border-t">
              {aging && (
                <div className="flex justify-between gap-2">
                  <span className="text-[#6B7280]">Umur Tunggakan</span>
                  <OverdueBadge inv={inv} tid="billing-inv-mobile-overdue" />
                </div>
              )}
              <div className="flex justify-between gap-2">
                <span className="text-[#6B7280]">Jatuh Tempo</span>
                <DueDateCell inv={inv} tid="billing-inv-mobile-due" />
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-[#6B7280]">Periode</span>
                <span>{periodeLabel(inv.periode_tagihan)}</span>
              </div>
              <div className="flex justify-between gap-2"
                   data-testid={`billing-inv-mobile-wilayah-${inv.invoice_id}`}>
                <span className="text-[#6B7280]">Wilayah</span>
                <span className="truncate max-w-[65%] text-right">
                  {inv.wilayah_id || <span className="text-[#9CA3AF]">—</span>}
                </span>
              </div>
              <div className="flex justify-between gap-2"
                   data-testid={`billing-inv-mobile-kolektor-${inv.invoice_id}`}>
                <span className="text-[#6B7280]">Kolektor</span>
                <span className="truncate max-w-[65%] text-right">
                  {inv.current_collector_name || <span className="text-[#9CA3AF]">—</span>}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-[#6B7280]">Tagihan</span>
                <span className="font-mono">{formatRupiah(inv.total_harus_dibayar)}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-[#6B7280]">Dibayar</span>
                <span className="font-mono">{formatRupiah(inv.total_dibayar)}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-[#6B7280]">Sisa</span>
                <span className={`font-mono ${inv.sisa_tagihan > 0 ? "text-[#991B1B]" : ""}`}>{formatRupiah(inv.sisa_tagihan)}</span>
              </div>
              <div className="flex justify-between gap-2"
                    data-testid={`billing-inv-mobile-receivers-${inv.invoice_id}`}>
                <span className="text-[#6B7280] flex-shrink-0">Penerima</span>
                <div className="text-right min-w-0 flex-1">
                  {!inv.payment_receivers || inv.payment_receivers.length === 0 ? (
                    <span className="text-[#9CA3AF]">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1 justify-end">
                      {inv.payment_receivers.map((r, idx) => {
                        const label = receivedLabel({
                          method: r.method,
                          received_role: r.received_role,
                          received_by_name_snapshot: r.received_by_name_snapshot,
                        }).label;
                        const cls = r.method === "TUNAI"
                          ? "bg-[#D1FAE5] text-[#065F46]"
                          : r.method === "KANTOR"
                            ? "bg-[#DBEAFE] text-[#1E3A8A]"
                            : "bg-[#FEF3C7] text-[#92400E]";
                        return (
                          <span key={idx}
                                className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${cls}`}
                                title={label}>
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1"
                      onClick={() => setDetail(inv)}
                      data-testid={`billing-inv-mobile-detail-${inv.invoice_id}`}>
                <FileText className="w-3 h-3 mr-1" /> Lihat Detail
              </Button>
              <Button size="sm" variant="outline" className="flex-1"
                      onClick={() => saveRowPdf(inv.invoice_id)}
                      disabled={savingPdf || (pdfRowId && pdfRowId !== inv.invoice_id) || pdfRowId === inv.invoice_id}
                      data-testid={`billing-inv-mobile-pdf-${inv.invoice_id}`}
                      title="Simpan invoice sebagai PDF" aria-label="Simpan invoice sebagai PDF">
                <FileDown className="w-3 h-3 mr-1" />
                {pdfRowId === inv.invoice_id ? "Menyimpan…" : "PDF"}
              </Button>
              {inv.status === "LUNAS" && Number(inv.sisa_tagihan) === 0 && (
                <Button size="sm" variant="outline" className="flex-1"
                        onClick={() => openReceipt(inv.invoice_id)}
                        disabled={!!receiptRowId}
                        data-testid={`billing-inv-mobile-receipt-${inv.invoice_id}`}
                        title="Lihat kwitansi pembayaran" aria-label="Lihat kwitansi pembayaran">
                  <ReceiptText className="w-3 h-3 mr-1" />
                  {receiptRowId === inv.invoice_id ? "Memuat…" : "Kwitansi"}
                </Button>
              )}
            </div>
          </div>
        ))}
        <NbInvoiceCards items={nbRows} onOpen={setNbOpenId} isAdmin={isAdmin} sel={nbSel} onToggle={toggleNbSelect}
                        pdfId={pdfRowId} pdfBusy={savingPdf} onPdf={saveNbRowPdf} rcptId={receiptRowId} onReceipt={openNbRowReceipt} />
      </div>

      {showGenerate && (
        <GenerateInvoiceDialog onClose={() => setShowGenerate(false)}
                                onDone={async () => { setShowGenerate(false); await load(); clearSelection(); }}
                                isAdmin={isAdmin}
                                collectorList={collectorList}
                                pageCollectorId={isAdmin ? collectorId : ""}
                                selectedCustomerIds={Array.from(new Set(rows.filter(r => selected.has(r.invoice_id)).map(r => r.customer_id).filter(Boolean)))} />
      )}
      {detail && (
        <InvoiceDetailDialog
          invoiceId={detail.invoice_id}
          isAdmin={isAdmin}
          onClose={() => setDetail(null)}
          onChanged={load}
          onOpenInvoicePdf={saveRowPdf}
          onOpenReceipt={openReceipt}
          savingInvoicePdf={pdfRowId === detail.invoice_id}
          openingReceipt={receiptRowId === detail.invoice_id}
        />
      )}

      {/* Sep 2026 Phase 19 — Bulk delete confirmation dialog */}
      <Dialog open={showBulkConfirm} onOpenChange={(v) => !v && setShowBulkConfirm(false)}>
        <DialogContent className="max-w-md" data-testid="billing-inv-bulk-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#991B1B]">
              <Trash2 className="w-5 h-5" /> Hapus Invoice?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              Anda akan menghapus <b>{selectionCount}</b> invoice yang dipilih.
              Tindakan ini <b>tidak dapat dibatalkan</b>.
            </p>
            {selectionCount > 0 && selectionCount <= 10 && (
              <div className="border rounded-md p-2 bg-[#F8F9FA] max-h-40 overflow-y-auto">
                <div className="text-[10px] uppercase font-bold text-[#6B7280] mb-1">Invoice yang akan dihapus:</div>
                <ul className="space-y-0.5 text-xs font-mono">
                  {Array.from(selected).map((id) => (
                    <li key={id} className="truncate">{id}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="text-[11px] text-[#6B7280] border-l-2 border-[#DC2626] pl-2">
              Invoice yang sudah memiliki pembayaran tidak akan terhapus.
              Void payment terlebih dahulu bila diperlukan.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkConfirm(false)}
                    disabled={bulkDeleting}
                    data-testid="billing-inv-bulk-cancel">
              Batal
            </Button>
            <Button onClick={runBulkDelete} disabled={bulkDeleting}
                    className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                    data-testid="billing-inv-bulk-confirm-btn">
              {bulkDeleting ? "Menghapus…" : `Hapus ${selectionCount} Invoice`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sep 2026 Phase 23a — Bulk Bayar dialog (ADMIN ONLY) */}
      {isAdmin && (
      <Dialog open={showBulkPay} onOpenChange={(v) => { if (!v && !bulkPaying) { setShowBulkPay(false); setBulkPayResult(null); } }}>
        <DialogContent className="max-w-lg" data-testid="billing-inv-bulk-pay-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5" /> Bayar Invoice Terpilih
            </DialogTitle>
          </DialogHeader>
          {!bulkPayResult ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 p-3 bg-[#F8F9FA] rounded border">
                <div>
                  <div className="text-[10px] uppercase font-bold text-[#6B7280]">Invoice Dipilih</div>
                  <div className="text-lg font-bold">{selectionCount}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase font-bold text-[#6B7280]">Valid Dibayar</div>
                  <div className="text-lg font-bold text-[#065F46]">{validForBulkPay().length}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase font-bold text-[#6B7280]">Akan Dilewati</div>
                  <div className="text-sm font-semibold text-[#B45309]">{selectionCount - validForBulkPay().length} (LUNAS/Dibatalkan/Carried)</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase font-bold text-[#6B7280]">Total Dibayar</div>
                  <div className="text-lg font-bold">{formatRupiah(bulkPayTotal())}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold">Tanggal Pembayaran</label>
                  <Input type="date" value={bulkPayForm.payment_date}
                         onChange={(e) => setBulkPayForm({ ...bulkPayForm, payment_date: e.target.value })}
                         disabled={bulkPaying}
                         data-testid="bulk-pay-date" />
                </div>
                <div>
                  <label className="text-xs font-semibold">Metode Pembayaran</label>
                  <select value={bulkPayForm.method}
                          onChange={(e) => setBulkPayForm({ ...bulkPayForm, method: e.target.value })}
                          disabled={bulkPaying}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                          data-testid="bulk-pay-method">
                    <option value="TUNAI">TUNAI (via Kolektor aktif)</option>
                    <option value="KANTOR">KANTOR (diterima admin)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold">Catatan (opsional)</label>
                <Input value={bulkPayForm.notes}
                       onChange={(e) => setBulkPayForm({ ...bulkPayForm, notes: e.target.value })}
                       placeholder="Contoh: setor tanggal 1 September"
                       maxLength={200}
                       disabled={bulkPaying}
                       data-testid="bulk-pay-notes" />
              </div>

              {validForBulkPay().length > 0 && validForBulkPay().length <= 20 && (
                <div className="border rounded-md p-2 bg-white max-h-40 overflow-y-auto">
                  <div className="text-[10px] uppercase font-bold text-[#6B7280] mb-1">Invoice yang akan dibayar:</div>
                  <ul className="space-y-0.5 text-xs font-mono">
                    {validForBulkPay().map((inv) => (
                      <li key={inv.invoice_id} className="flex justify-between">
                        <span>{inv.invoice_id} · {inv.customer_name_snapshot || inv.customer_id}</span>
                        <span className="font-bold">{formatRupiah(inv.sisa_tagihan)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="text-[11px] text-[#6B7280] border-l-2 border-[#3B82F6] pl-2">
                Setiap invoice akan dibayar penuh (`pay_full`) menggunakan sisa tagihan aktual dari backend.
                Invoice yang sudah LUNAS/DIBATALKAN otomatis dilewati.
                {bulkPayForm.method === "TUNAI" && " Penerima uang otomatis: kolektor aktif customer masing-masing."}
                {bulkPayForm.method === "KANTOR" && " Penerima uang: admin yang mencatat (Anda)."}
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-sm" data-testid="bulk-pay-result">
              <div className="p-3 rounded border bg-[#F8F9FA]">
                <div className="font-semibold mb-1">Bulk pembayaran selesai.</div>
                <div className="text-[#065F46]">✓ {bulkPayResult.success.length} invoice berhasil.</div>
                {bulkPayResult.fail.length > 0 && (
                  <div className="text-[#991B1B]">✗ {bulkPayResult.fail.length} invoice gagal.</div>
                )}
              </div>
              {bulkPayResult.fail.length > 0 && (
                <div className="border rounded p-2 bg-[#FEF2F2] max-h-48 overflow-y-auto">
                  <div className="text-[10px] uppercase font-bold text-[#991B1B] mb-1">Invoice yang gagal:</div>
                  <ul className="space-y-1 text-xs">
                    {bulkPayResult.fail.map((f) => (
                      <li key={f.invoice_id} className="border-b border-[#FECACA] pb-1">
                        <div className="font-mono">{f.invoice_id} · {f.customer}</div>
                        <div className="italic text-[#B91C1C]">{f.error}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {!bulkPayResult ? (
              <>
                <Button variant="outline" onClick={() => setShowBulkPay(false)}
                        disabled={bulkPaying}
                        data-testid="bulk-pay-cancel-btn">
                  Batal
                </Button>
                <Button onClick={runBulkPay}
                        disabled={bulkPaying || validForBulkPay().length === 0}
                        data-testid="bulk-pay-confirm-btn">
                  {bulkPaying ? "Memproses…" : `Konfirmasi Bayar (${validForBulkPay().length})`}
                </Button>
              </>
            ) : (
              <Button onClick={() => { setShowBulkPay(false); setBulkPayResult(null); }}
                      data-testid="bulk-pay-close-btn">
                Tutup
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {showBulkVoidMulti && isAdmin && (
        <BulkVoidMultiInvoiceDialog
          invoiceIds={Array.from(selected)}
          onClose={() => setShowBulkVoidMulti(false)}
          onDone={async () => { setShowBulkVoidMulti(false); clearSelection(); await load(); }}
        />
      )}

    </div>
  );
}


function formatEtaSeconds(sec) {
  // Sep 2026 Phase 22i — format ETA jadi "5 detik" / "1 mnt 20 dtk"
  // supaya mudah dibaca admin. Null → "…"
  if (sec === null || sec === undefined) return "…";
  if (sec <= 0) return "<1 detik";
  if (sec < 60) return `${sec} detik`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (s === 0) return `${m} menit`;
  return `${m} mnt ${s} dtk`;
}


function playCompletionBell(kind = "success") {
  // Sep 2026 Phase 22j — bell ringan pakai Web Audio API (tanpa asset
  // eksternal). `kind === "success"` → dua-nada naik (A5 → D6);
  // `kind === "info"` → satu-nada netral untuk cancel. Silent bila
  // AudioContext tidak tersedia atau tab di-mute.
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const notes = kind === "success"
      ? [[880, 0], [1174.66, 0.13]]   // A5 → D6 (naik)
      : [[660, 0]];                     // E5 (netral)
    for (const [freq, start] of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const t0 = now + start;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.15, t0 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.4);
    }
    setTimeout(() => { try { ctx.close(); } catch { /* no-op */ } }, 800);
  } catch { /* silent — bell adalah nice-to-have, tidak boleh crash flow */ }
}


function GenerateInvoiceDialog({ onClose, onDone, isAdmin = false, collectorList = [], pageCollectorId = "", selectedCustomerIds = [] }) {
  const now = new Date();
  const yy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const [periode, setPeriode] = useState(`${yy}-${mm}`);
  const [tgl, setTgl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [aktifCount, setAktifCount] = useState(null);
  // Feb 2026 — 3 Mode Generate. Scope resolver ONLY; engine `generate_invoices`
  // + duplicate detection existing tetap Single Source of Truth.
  //   ALL       → semua customer AKTIF (behavior existing)
  //   COLLECTOR → customer dgn assignment AKTIF ke collector terpilih
  //   CHECKLIST → customer_ids yang di-centang di tabel invoice (deduped)
  const [mode, setMode] = useState("ALL");
  const [collectorId, setCollectorId] = useState("");
  // Sep 2026 Phase 22e — live progress state. Pagination doubles as
  // chunk boundary: fetch page of 200 → generate for those 200 → repeat.
  // Zero backend changes; business logic invariant Billing V1 utuh.
  const [progress, setProgress] = useState(null); // {done, total, batch, etaSec}
  // Sep 2026 Phase 22i — ETA berdasarkan rate rolling (customers/detik).
  // Menggunakan waktu global sejak submit dimulai (bukan window) supaya
  // sederhana & stabil. Jitter parallelism di-smooth via floor 3 detik
  // minimum sample sebelum mulai menampilkan estimasi.
  const startTsRef = useRef(0);
  // Sep 2026 Phase 22j — sound toggle. Persistensi ringan via localStorage
  // supaya preferensi admin tidak reset tiap buka dialog. Default ON.
  const [soundOn, setSoundOn] = useState(() => {
    try {
      const v = localStorage.getItem("aroma_billing_gen_sound");
      return v === null ? true : v === "1";
    } catch { return true; }
  });
  const setSoundOnPersist = (v) => {
    setSoundOn(v);
    try { localStorage.setItem("aroma_billing_gen_sound", v ? "1" : "0"); } catch { /* no-op */ }
  };
  // Sep 2026 Phase 22f — cancel support. Ref agar cek terbaru di async
  // loop tidak stale. `AbortController` men-cancel request in-flight
  // seketika; loop di-break setelah chunk selesai/abort supaya business
  // logic per-chunk tetap atomik (idempoten, unique index melindungi).
  const cancelRef = useRef(false);
  const abortRef = useRef(null);
  const [canceling, setCanceling] = useState(false);
  // Sep 2026 Phase 22h — flag `isResumeRun` men-trigger pre-fetch existing
  // invoice customer_ids untuk periode; loop utama akan filter tiap page
  // dan skip generate bila semua sudah punya invoice. Prefetch cost
  // sebanding dengan existing count (paginated 200/page), tapi ergonomi
  // "skip terlihat cepat" jauh lebih baik terutama untuk resume yang
  // near-complete.
  const isResumeRunRef = useRef(false);

  // Feb 2026 — CHECKLIST customer picker in-dialog. Admin tidak perlu
  // kembali ke tabel invoice untuk centang customer. Source:
  //   - `pageCollectorId` dari halaman Invoice (SSoT filter kolektor;
  //     tidak ada dropdown kedua di dialog).
  //   - `pickerQuery` search nama/customer_id.
  //   - `pickerIds` Set berbasis customer_id, stabil terhadap search.
  // Seeded dari `selectedCustomerIds` (bila user memang sudah centang
  // di tabel) supaya backward-compatible.
  const [pickerIds, setPickerIds] = useState(() => new Set(selectedCustomerIds || []));
  const [pickerList, setPickerList] = useState([]);
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerLoading, setPickerLoading] = useState(false);
  const effectiveSelectedIds = Array.from(pickerIds);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (mode === "CHECKLIST") {
          if (alive) setAktifCount(effectiveSelectedIds.length);
          return;
        }
        const params = { status_layanan: "AKTIF", limit: 1, page: 1 };
        if (mode === "COLLECTOR" && collectorId) params.collector_user_id = collectorId;
        const { data } = await api.get("/billing/customers", { params });
        if (alive) setAktifCount(data.total ?? 0);
      } catch { if (alive) setAktifCount(0); }
    })();
    return () => { alive = false; };
  }, [mode, collectorId, effectiveSelectedIds.length]);

  const requestCancel = () => {
    cancelRef.current = true;
    setCanceling(true);
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* no-op */ }
    }
  };

  const submit = async () => {
    if (busy) return;
    cancelRef.current = false;
    setCanceling(false);
    setBusy(true);
    startTsRef.current = Date.now();
    // Aggregate hasil dari semua chunk. Struktur response identik dengan
    // endpoint single-shot supaya UI hasil tidak berubah.
    const agg = {
      periode_tagihan: periode,
      created: 0,
      skipped_duplicate: 0,
      skipped_duplicate_carried: 0,
      skipped_nonaktif: 0,
      invoice_ids: [],
      canceled: false,
    };
    const PAGE_SIZE = 200; // = server max limit; sekaligus batch size fast-path
    try {
      // Feb 2026 — Fast path CHECKLIST: langsung POST /generate dengan
      // customer_ids yang sudah dipilih (deduped di parent). Backend engine
      // existing menangani filter AKTIF + duplicate detection sebagai SoT.
      if (mode === "CHECKLIST") {
        const ids = effectiveSelectedIds;
        if (ids.length === 0) {
          toast.error("Pilih minimal 1 customer terlebih dahulu.");
          setBusy(false);
          return;
        }
        setProgress({ done: 0, total: ids.length, batch: ids.length, etaSec: null });
        const body = { periode_tagihan: periode, customer_ids: ids };
        if (tgl) body.tanggal_jatuh_tempo = tgl;
        abortRef.current = new AbortController();
        const { data } = await api.post("/billing/invoices/generate", body, {
          signal: abortRef.current.signal,
        });
        agg.created = data.created || 0;
        agg.skipped_duplicate = data.skipped_duplicate || 0;
        agg.skipped_duplicate_carried = data.skipped_duplicate_carried || 0;
        agg.skipped_nonaktif = data.skipped_nonaktif || 0;
        if (Array.isArray(data.invoice_ids)) agg.invoice_ids.push(...data.invoice_ids);
        setProgress({ done: ids.length, total: ids.length, batch: ids.length, etaSec: 0 });
        setResult(agg);
        setBusy(false);
        return;
      }
      // Ambil halaman pertama untuk tahu total real (may drift dari aktifCount)
      abortRef.current = new AbortController();
      const listParams = { status_layanan: "AKTIF", limit: PAGE_SIZE, page: 1 };
      if (mode === "COLLECTOR" && collectorId) listParams.collector_user_id = collectorId;
      const firstResp = await api.get("/billing/customers", {
        params: listParams,
        signal: abortRef.current.signal,
      });
      const total = firstResp.data.total ?? 0;
      const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
      let done = 0;

      const existingCidSet = new Set();

      // Sep 2026 Phase 22i — hitung ETA saat progress ter-update. Rate
      // pakai elapsed global (customer/detik). Threshold minimum sample
      // 400ms untuk menghindari estimasi liar di awal ketika denominator
      // masih sangat kecil.
      const computeEta = (doneNow) => {
        const elapsedMs = Date.now() - startTsRef.current;
        if (elapsedMs < 400 || doneNow < 1) return null;
        const rate = doneNow / (elapsedMs / 1000); // customers per second
        if (rate <= 0) return null;
        const remaining = Math.max(0, total - doneNow);
        return Math.max(0, Math.round(remaining / rate));
      };

      const runPage = async (pageData) => {
        const ids = (pageData.items || []).map((c) => c.customer_id).filter(Boolean);
        if (ids.length === 0) return;
        // Sep 2026 Phase 22h — filter customer yang sudah punya invoice
        // periode ini (dari prefetch resume) supaya bisa skip generate
        // call yang seluruhnya duplicate.
        const missing = existingCidSet.size > 0
          ? ids.filter((cid) => !existingCidSet.has(cid))
          : ids;
        if (missing.length === 0) {
          // Skip generate — semua customer di page sudah punya invoice.
          agg.skipped_duplicate += ids.length;
          done += ids.length;
          setProgress({ done, total, batch: 0, etaSec: computeEta(done) });
          return;
        }
        const body = {
          periode_tagihan: periode,
          customer_ids: missing,
        };
        if (tgl) body.tanggal_jatuh_tempo = tgl;
        abortRef.current = new AbortController();
        const { data } = await api.post("/billing/invoices/generate", body, {
          signal: abortRef.current.signal,
        });
        agg.created += data.created || 0;
        // Sertakan customer yang di-prefilter ke skipped_duplicate agar
        // total aggregate akurat.
        agg.skipped_duplicate += (data.skipped_duplicate || 0) + (ids.length - missing.length);
        agg.skipped_duplicate_carried += data.skipped_duplicate_carried || 0;
        agg.skipped_nonaktif += data.skipped_nonaktif || 0;
        if (Array.isArray(data.invoice_ids)) agg.invoice_ids.push(...data.invoice_ids);
        // Perbarui set supaya iterasi berikutnya konsisten (jaga-jaga
        // duplicate listing antar page — rare).
        for (const cid of missing) existingCidSet.add(cid);
        done += ids.length;
        setProgress({ done, total, batch: missing.length, etaSec: computeEta(done) });
      };

      setProgress({ done: 0, total, batch: 0, etaSec: null });

      // Sep 2026 Phase 22h — prefetch existing customer_ids untuk periode
      // (hanya bila resume run). Loop utama akan filter tiap page dan
      // skip generate bila 0 customer tersisa. Prefetch pakai
      // `include_carried=true` supaya invoice ter-konsolidasi juga
      // dihitung "sudah punya" (konsisten dengan unique index Billing V1
      // yang memblokir duplicate periode+customer di semua status).
      //
      // Optimasi: gunakan page 1 untuk baca `total`, lalu paralelkan
      // sisa halaman (concurrency=4). Cancel-safe via cancelRef check.
      if (isResumeRunRef.current) {
        abortRef.current = new AbortController();
        const firstEx = await api.get("/billing/invoices", {
          params: {
            periode_tagihan: periode,
            include_carried: true,
            limit: PAGE_SIZE,
            page: 1,
          },
          signal: abortRef.current.signal,
        });
        const exTotal = firstEx.data.total || 0;
        const exTotalPages = Math.max(1, Math.ceil(exTotal / PAGE_SIZE));
        for (const it of (firstEx.data.items || [])) {
          if (it.customer_id) existingCidSet.add(it.customer_id);
        }
        // Parallel fetch page 2..exTotalPages
        if (exTotalPages > 1) {
          let nextEp = 2;
          const CONCURRENCY = 4;
          const worker = async () => {
            while (nextEp <= exTotalPages) {
              if (cancelRef.current) return;
              const my = nextEp;
              nextEp += 1;
              abortRef.current = new AbortController();
              // eslint-disable-next-line no-await-in-loop
              const { data: ex } = await api.get("/billing/invoices", {
                params: {
                  periode_tagihan: periode,
                  include_carried: true,
                  limit: PAGE_SIZE,
                  page: my,
                },
                signal: abortRef.current.signal,
              });
              for (const it of (ex.items || [])) {
                if (it.customer_id) existingCidSet.add(it.customer_id);
              }
            }
          };
          await Promise.all(
            Array.from({ length: Math.min(CONCURRENCY, exTotalPages - 1) }, worker)
          );
        }
      }

      if (!cancelRef.current) {
        await runPage(firstResp.data);
      }
      // Parallel fetch + runPage untuk page 2..totalPages. Fetch customer
      // page & runPage per worker; concurrency = 4 (bagus untuk latency
      // preview backend ~30ms per request, kurang agresif dari 8+ yang
      // bisa memicu rate-limit).
      if (totalPages > 1) {
        let nextP = 2;
        const CONCURRENCY = 4;
        const worker = async () => {
          while (nextP <= totalPages) {
            if (cancelRef.current) return;
            const my = nextP;
            nextP += 1;
            abortRef.current = new AbortController();
            const pParams = { status_layanan: "AKTIF", limit: PAGE_SIZE, page: my };
            if (mode === "COLLECTOR" && collectorId) pParams.collector_user_id = collectorId;
            // eslint-disable-next-line no-await-in-loop
            const { data: pd } = await api.get("/billing/customers", {
              params: pParams,
              signal: abortRef.current.signal,
            });
            if (cancelRef.current) return;
            // eslint-disable-next-line no-await-in-loop
            await runPage(pd);
          }
        };
        await Promise.all(
          Array.from({ length: Math.min(CONCURRENCY, totalPages - 1) }, worker)
        );
      }
      if (cancelRef.current) {
        agg.canceled = true;
        setResult(agg);
        if (soundOn) playCompletionBell("info");
        toast.info(`Dibatalkan. ${agg.created} invoice sempat dibuat sebelum berhenti.`);
      } else {
        setResult(agg);
        if (soundOn) playCompletionBell("success");
        toast.success(`Generate periode ${periode}: ${agg.created} invoice baru`);
      }
    } catch (err) {
      // Cancel via AbortController → axios throws CanceledError. Treat as
      // cancel, tampilkan hasil parsial.
      const isCancel = err?.name === "CanceledError" || err?.code === "ERR_CANCELED"
        || err?.message === "canceled";
      if (isCancel || cancelRef.current) {
        agg.canceled = true;
        setResult(agg);
        if (soundOn) playCompletionBell("info");
        toast.info(`Dibatalkan. ${agg.created} invoice sempat dibuat sebelum berhenti.`);
      } else {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal generate invoice");
      }
    } finally {
      abortRef.current = null;
      setCanceling(false);
      setBusy(false);
      setProgress(null);
      isResumeRunRef.current = false;
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="billing-generate-dialog">
        <DialogHeader><DialogTitle>Generate Invoice</DialogTitle></DialogHeader>
        {!result && (
          <div className="space-y-3 text-sm">
            <div><Label>Periode Tagihan</Label>
              <Input type="month" value={periode} onChange={(e) => setPeriode(e.target.value)} required
                     data-testid="gen-periode" /></div>
            <div><Label>Tanggal Jatuh Tempo (opsional)</Label>
              <Input type="date" value={tgl} onChange={(e) => setTgl(e.target.value)} /></div>

            {/* Feb 2026 — 3 Mode Generate. Scope resolver saja; engine +
                duplicate detection existing tetap SoT. */}
            <div className="border rounded-md p-3 space-y-2 bg-[#F9FAFB]" data-testid="gen-mode-group">
              <div className="text-xs font-semibold text-[#374151]">Mode Generate</div>
              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input type="radio" name="gen-mode" value="ALL"
                       checked={mode === "ALL"}
                       onChange={() => { setMode("ALL"); setConfirmed(false); }}
                       disabled={busy}
                       className="mt-0.5"
                       data-testid="gen-mode-all" />
                <div>
                  <div className="text-sm font-medium">Semua Customer Aktif</div>
                  <div className="text-[11px] text-[#6B7280]">Behavior existing — semua customer AKTIF diperiksa.</div>
                </div>
              </label>
              {isAdmin && (
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input type="radio" name="gen-mode" value="COLLECTOR"
                         checked={mode === "COLLECTOR"}
                         onChange={() => { setMode("COLLECTOR"); setConfirmed(false); }}
                         disabled={busy}
                         className="mt-0.5"
                         data-testid="gen-mode-collector" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Menurut Kolektor</div>
                    <div className="text-[11px] text-[#6B7280]">Hanya customer dgn assignment AKTIF ke kolektor terpilih.</div>
                    {mode === "COLLECTOR" && (
                      <select value={collectorId}
                              onChange={(e) => { setCollectorId(e.target.value); setConfirmed(false); }}
                              disabled={busy}
                              className="mt-1.5 w-full border rounded-md px-2 py-1 text-xs bg-white"
                              data-testid="gen-collector-select">
                        <option value="">Pilih Kolektor…</option>
                        {collectorList.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </label>
              )}
              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input type="radio" name="gen-mode" value="CHECKLIST"
                       checked={mode === "CHECKLIST"}
                       onChange={() => { setMode("CHECKLIST"); setConfirmed(false); }}
                       disabled={busy}
                       className="mt-0.5"
                       data-testid="gen-mode-checklist" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Berdasarkan Checklist</div>
                  <div className="text-[11px] text-[#6B7280]">
                    Customer yg di-centang di tabel invoice (deduped per customer_id).
                    Checklist bukan force-generate — aturan duplicate tetap berlaku.
                  </div>
                  {mode === "CHECKLIST" && (
                    <CustomerPicker
                      pageCollectorId={pageCollectorId}
                      collectorList={collectorList}
                      pickerIds={pickerIds}
                      setPickerIds={setPickerIds}
                      query={pickerQuery}
                      setQuery={setPickerQuery}
                      list={pickerList}
                      setList={setPickerList}
                      loading={pickerLoading}
                      setLoading={setPickerLoading}
                      disabled={busy}
                    />
                  )}
                </div>
              </label>
            </div>

            {!confirmed ? (
              <div className="text-xs bg-[#EFF6FF] border border-[#BFDBFE] rounded p-2" data-testid="gen-preview">
                {mode === "ALL" && (
                  <>Sistem akan memeriksa{" "}
                  <b data-testid="gen-aktif-count">{aktifCount === null ? "…" : `${aktifCount} customer AKTIF`}</b>{" "}
                  dan membuat invoice periode <b>{periodeLabel(periode)}</b> untuk yang belum memilikinya.
                  Customer BERHENTI/BERHENTI_SEMENTARA dilewati. Duplikasi dicegah otomatis (idempotent).</>
                )}
                {mode === "COLLECTOR" && (
                  <>Sistem akan memeriksa{" "}
                  <b data-testid="gen-aktif-count">{aktifCount === null ? "…" : `${aktifCount} customer`}</b>{" "}
                  dgn assignment AKTIF ke kolektor <b>{(collectorList.find(c => c.id === collectorId) || {}).name || "—"}</b>
                  {" "}dan membuat invoice periode <b>{periodeLabel(periode)}</b> yang belum ada. Aturan BERHENTI + duplicate tetap dijaga.</>
                )}
                {mode === "CHECKLIST" && (
                  <>Sistem akan memproses <b data-testid="gen-aktif-count">{effectiveSelectedIds.length} customer unik</b>{" "}
                  yg dipilih untuk periode <b>{periodeLabel(periode)}</b>. Duplicate + status BERHENTI tetap disaring oleh engine.</>
                )}
              </div>
            ) : null}
            <div className="flex items-center justify-between text-xs text-[#374151] pt-1">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="gen-sound-label">
                <input
                  type="checkbox"
                  checked={soundOn}
                  onChange={(e) => setSoundOnPersist(e.target.checked)}
                  disabled={busy}
                  className="h-3.5 w-3.5"
                  data-testid="gen-sound-toggle"
                />
                <span aria-hidden="true">{soundOn ? "🔔" : "🔕"}</span>
                <span>Bunyi selesai</span>
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Batal</Button>
              {!confirmed ? (
                <Button type="button" onClick={() => setConfirmed(true)}
                        disabled={
                          (mode === "COLLECTOR" && !collectorId) ||
                          (mode === "CHECKLIST" && effectiveSelectedIds.length === 0)
                        }
                        data-testid="gen-confirm-btn">Lanjut</Button>
              ) : !busy ? (
                <Button type="button" onClick={submit} disabled={busy} data-testid="gen-submit">
                  {mode === "CHECKLIST"
                    ? `Generate ${effectiveSelectedIds.length} Customer Terpilih`
                    : `Generate ${aktifCount !== null ? aktifCount + " " : ""}Customer${mode === "COLLECTOR" ? "" : " Aktif"}`}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={requestCancel}
                  disabled={canceling}
                  data-testid="gen-cancel-btn"
                >
                  {canceling ? "Membatalkan…" : "Batalkan"}
                </Button>
              )}
            </DialogFooter>
            {progress && (
              <div className="mt-3 space-y-1.5" data-testid="gen-progress">
                <div className="flex items-center justify-between text-xs text-[#374151]">
                  <span>
                    Memproses:{" "}
                    <b data-testid="gen-progress-done">{progress.done.toLocaleString("id-ID")}</b>
                    {" / "}
                    <b data-testid="gen-progress-total">{progress.total.toLocaleString("id-ID")}</b>
                    {" customer"}
                  </span>
                  <span className="tabular-nums" data-testid="gen-progress-percent">
                    {progress.total > 0
                      ? `${Math.min(100, Math.round((progress.done / progress.total) * 100))}%`
                      : "…"}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-[#E5E7EB] overflow-hidden">
                  <div
                    className="h-full bg-[#2563EB] transition-[width] duration-300 ease-out"
                    style={{
                      width: progress.total > 0
                        ? `${Math.min(100, (progress.done / progress.total) * 100)}%`
                        : "0%",
                    }}
                    data-testid="gen-progress-bar"
                  />
                </div>
                {progress.batch > 0 && (
                  <div className="text-[11px] text-[#6B7280] flex items-center justify-between">
                    <span>Batch terakhir: {progress.batch} customer</span>
                    <span data-testid="gen-progress-eta">
                      Sisa waktu: <b>{formatEtaSeconds(progress.etaSec)}</b>
                    </span>
                  </div>
                )}
                {progress.batch === 0 && progress.done > 0 && (
                  <div className="text-[11px] text-[#6B7280] flex items-center justify-between">
                    <span className="text-[#10B981]">Page ini sudah punya invoice · dilewati</span>
                    <span data-testid="gen-progress-eta">
                      Sisa waktu: <b>{formatEtaSeconds(progress.etaSec)}</b>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {result && (
          <div className="space-y-2 text-sm" data-testid="gen-result">
            <div>Periode: <b>{periodeLabel(result.periode_tagihan)}</b></div>
            {result.canceled && (
              <div
                className="text-xs bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E] rounded p-2"
                data-testid="gen-result-canceled"
              >
                Proses dibatalkan sebelum semua customer selesai. Invoice yang sempat dibuat sudah tersimpan (idempoten — aman untuk di-generate ulang bila perlu).
              </div>
            )}
            <div>Invoice baru dibuat: <b data-testid="gen-result-created">{result.created}</b></div>
            <div>Invoice aktif sudah ada: <b data-testid="gen-result-dup-active">{result.skipped_duplicate}</b></div>
            <div>Invoice sudah dikonsolidasi/carry-forward: <b data-testid="gen-result-dup-carried">{result.skipped_duplicate_carried ?? 0}</b></div>
            <div>Customer nonaktif dilewati: <b>{result.skipped_nonaktif}</b></div>
            <div className="pt-1 border-t text-[11px] text-[#6B7280]">
              Total tidak dibuat: {(result.skipped_duplicate || 0) + (result.skipped_duplicate_carried || 0) + (result.skipped_nonaktif || 0)}
            </div>
            <DialogFooter>
              {result.canceled && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    isResumeRunRef.current = true;
                    setResult(null);
                    submit();
                  }}
                  data-testid="gen-resume-btn"
                >
                  Lanjutkan dari yang tertinggal
                </Button>
              )}
              <Button onClick={onDone}>Tutup</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function InvoiceDetailDialog({ invoiceId, isAdmin, onClose, onChanged,
                              onOpenInvoicePdf, onOpenReceipt,
                              savingInvoicePdf = false, openingReceipt = false }) {
  const [inv, setInv] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  // Sep 2026 Phase 22q — invoice branding (logo + slogan) dari settings.
  // Fetch sekali per open dialog; fallback aman bila belum diatur.
  const [branding, setBranding] = useState({ logo_data_url: "", slogan: "" });
  useEffect(() => {
    api.get("/settings/invoice-branding")
      .then(({ data }) => setBranding(data || {}))
      .catch(() => {});
  }, []);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [voidingId, setVoidingId] = useState(null);
  // Feb 2026 — Admin edit payment method tanpa VOID (koreksi data entry).
  // Target payment untuk edit method; dialog muncul saat non-null.
  const [editMethodTarget, setEditMethodTarget] = useState(null);
  // Feb 2026 — Bulk Void Payment di invoice detail. Checkbox hanya
  // muncul saat invoice LUNAS/SEBAGIAN & admin & payment CONFIRMED.
  // Selection berbasis payment_id, auto-clear saat reload.
  const [voidSel, setVoidSel] = useState(new Set());
  const [bulkVoidOpen, setBulkVoidOpen] = useState(false);
  // Feb 2026 — Items/Barang Tambahan (ADMIN ONLY). Nama bebas,
  // nominal Rupiah. Add/Edit/Delete recompute total via engine SSoT
  // backend; frontend hanya kirim mutasi.
  const [itemFormOpen, setItemFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItemId, setDeletingItemId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [i, p] = await Promise.all([
        api.get(`/billing/invoices/${invoiceId}`),
        api.get("/billing/payments", { params: { invoice_id: invoiceId, limit: 200 } }),
      ]);
      setInv(i.data);
      setPayments(p.data.items || []);
      setVoidSel(new Set());  // clear bulk-void selection after reload
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [invoiceId]);

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="billing-inv-detail-dialog">
        <DialogHeader><DialogTitle>Detail Invoice</DialogTitle></DialogHeader>
        {loading && <div className="text-sm">Memuat…</div>}
        {inv && (
          <div className="space-y-3 text-sm">
            {/* Sep 2026 Phase 22q — Header branding invoice. Logo kiri +
                Slogan di bawah. Fallback tanpa logo: hanya tampilkan
                nama perusahaan + subtitle. */}
            <div className="flex items-start gap-3 pb-3 border-b" data-testid="inv-branding-header">
              {branding.logo_data_url ? (
                <img
                  src={branding.logo_data_url}
                  alt="Logo perusahaan"
                  className="h-12 w-auto max-w-[120px] object-contain flex-shrink-0"
                  data-testid="inv-branding-logo"
                />
              ) : null}
              <div className="flex-1 min-w-0">
                <div className="text-base font-black tracking-tight leading-tight">AROMA NET</div>
                <div className="text-[11px] text-[#6B7280]">Sistem Billing Internet</div>
                {branding.slogan && (
                  <div className="text-xs text-[#374151] italic mt-0.5" data-testid="inv-branding-slogan">
                    {branding.slogan}
                  </div>
                )}
              </div>
            </div>
            <div className="border rounded p-3 bg-[#F8F9FA]">
              <div className="flex justify-between">
                <div>
                  <div className="font-mono text-xs text-[#6B7280]">{inv.invoice_id}</div>
                  <div className="font-semibold">{inv.customer_name_snapshot}</div>
                  <div className="text-xs">{periodeLabel(inv.periode_tagihan)} · {inv.package_name_snapshot}</div>
                </div>
                <Badge className={`text-xs ${STATUS_COLOR[inv.status] || ""}`}
                       data-testid="inv-detail-status">
                  {STATUS_LABEL[inv.status] || inv.status}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <Row label="Harga Paket" value={formatRupiah(inv.harga_paket_snapshot)} />
              <Row label="Diskon" value={formatRupiah(inv.diskon)} />
              {(inv.items || []).length > 0 && (
                <Row label="Items Tambahan" value={formatRupiah((inv.items || []).reduce((s,it)=>s+Number(it.amount||0),0))} />
              )}
              <Row label="Total Tagihan" value={formatRupiah(inv.total_tagihan)} />
              <Row label="Bon Sebelumnya" value={formatRupiah(inv.bon_previous_amount)} />
              <Row label="Total Harus Dibayar" value={formatRupiah(inv.total_harus_dibayar)} bold />
              <Row label="Total Dibayar" value={formatRupiah(inv.total_dibayar)} />
              <Row label="Sisa" value={formatRupiah(inv.sisa_tagihan)} bold />
              <Row label="Jatuh Tempo" value={<DueDateCell inv={inv} tid="inv-detail-due" />} />
            </div>

            <BonComponents data={inv.bon_components} />

            {/* Feb 2026 — Barang Tambahan (nama bebas, bukan master item).
                Admin dapat Add/Edit/Delete selama invoice bukan DIBATALKAN
                dan belum di-carry-forward. Pembayaran existing tidak
                disentuh — total/sisa/status di-recompute oleh backend. */}
            {(isAdmin || (inv.items || []).length > 0) && inv.status !== "DIBATALKAN" && !inv.carry_forward_to && (
              <div className="border rounded p-2 bg-[#F8FAFC]" data-testid="inv-items-section">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold text-xs">Barang Tambahan ({(inv.items || []).length})</div>
                  {isAdmin && (
                    <Button size="sm" variant="outline"
                            onClick={() => { setEditingItem(null); setItemFormOpen(true); }}
                            data-testid="inv-item-add-btn">
                      <Plus className="w-3 h-3 mr-1" /> Tambah Item
                    </Button>
                  )}
                </div>
                {(inv.items || []).length === 0 ? (
                  <div className="text-[11px] text-[#6B7280]">Belum ada barang tambahan.</div>
                ) : (
                  <ul className="space-y-1">
                    {(inv.items || []).map((it) => (
                      <li key={it.item_id}
                          className="flex items-center justify-between gap-2 text-xs bg-white border rounded px-2 py-1"
                          data-testid={`inv-item-${it.item_id}`}>
                        <span className="truncate" title={it.name} data-testid={`inv-item-name-${it.item_id}`}>{it.name}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="font-mono" data-testid={`inv-item-amount-${it.item_id}`}>{formatRupiah(it.amount)}</span>
                          {isAdmin && (
                            <>
                              <Button size="sm" variant="outline" className="h-6 px-1.5"
                                      onClick={() => { setEditingItem(it); setItemFormOpen(true); }}
                                      data-testid={`inv-item-edit-${it.item_id}`}
                                      aria-label={`Edit item ${it.name}`} title="Edit item">
                                <FileText className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="outline" className="h-6 px-1.5 text-[#991B1B]"
                                      onClick={() => setDeletingItemId(it.item_id)}
                                      data-testid={`inv-item-delete-${it.item_id}`}
                                      aria-label={`Hapus item ${it.name}`} title="Hapus item">
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {/* Read-only view untuk invoice DIBATALKAN/carry-forward kalau punya items */}
            {(inv.items || []).length > 0 && (inv.status === "DIBATALKAN" || inv.carry_forward_to) && (
              <div className="border rounded p-2 bg-[#F3F4F6]" data-testid="inv-items-section-readonly">
                <div className="font-semibold text-xs mb-1">Barang Tambahan ({(inv.items || []).length})</div>
                <ul className="space-y-1">
                  {(inv.items || []).map((it) => (
                    <li key={it.item_id} className="flex items-center justify-between text-xs bg-white border rounded px-2 py-1">
                      <span className="truncate" title={it.name}>{it.name}</span>
                      <span className="font-mono">{formatRupiah(it.amount)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {inv.bon_previous_amount > 0 && (
              <div className="text-xs bg-[#FFFBEB] border border-[#FDE68A] rounded p-2">
                Termasuk bon: {formatRupiah(inv.bon_previous_amount)} dari invoice sebelumnya
                {inv.bon_source_invoice_ids?.length ? ` (${inv.bon_source_invoice_ids.join(", ")})` : ""}.
              </div>
            )}
            {inv.carry_forward_to && (
              <div className="text-xs bg-[#F3F4F6] border rounded p-2">
                Sisa invoice ini sudah dialihkan sebagai bon ke <b className="font-mono">{inv.carry_forward_to}</b> (Rp{Number(inv.carry_forward_amount || 0).toLocaleString("id-ID")}).
              </div>
            )}

            {isAdmin && inv.status !== "DIBATALKAN" && !inv.carry_forward_to && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => setShowPay(true)}
                        data-testid="inv-admin-pay-btn"
                        disabled={inv.status === "LUNAS"}
                        title={inv.status === "LUNAS" ? "Invoice sudah LUNAS" : ""}>
                  <Wallet className="w-3 h-3 mr-1" /> Catat Pembayaran
                </Button>
                <Button size="sm" onClick={() => setShowDiscount(true)}
                        data-testid="inv-discount-btn">
                  <Percent className="w-3 h-3 mr-1" /> Edit Diskon
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowCancel(true)}
                        data-testid="inv-cancel-btn" disabled={inv.total_dibayar > 0}
                        title={inv.total_dibayar > 0 ? "Void payment terlebih dahulu" : ""}>
                  <Ban className="w-3 h-3 mr-1" /> Batalkan Invoice
                </Button>
                <Button size="sm" variant="outline"
                        onClick={() => onOpenInvoicePdf && onOpenInvoicePdf(inv.invoice_id)}
                        disabled={savingInvoicePdf}
                        data-testid={`inv-detail-pdf-btn-${inv.invoice_id}`}
                        title="Simpan invoice sebagai PDF" aria-label="Simpan invoice sebagai PDF">
                  <FileDown className="w-3 h-3 mr-1" />
                  {savingInvoicePdf ? "Menyimpan…" : "PDF Invoice"}
                </Button>
              </div>
            )}
            {(!isAdmin || inv.status === "DIBATALKAN" || inv.carry_forward_to) && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline"
                        onClick={() => onOpenInvoicePdf && onOpenInvoicePdf(inv.invoice_id)}
                        disabled={savingInvoicePdf}
                        data-testid={`inv-detail-pdf-btn-${inv.invoice_id}`}
                        title="Simpan invoice sebagai PDF" aria-label="Simpan invoice sebagai PDF">
                  <FileDown className="w-3 h-3 mr-1" />
                  {savingInvoicePdf ? "Menyimpan…" : "PDF Invoice"}
                </Button>
              </div>
            )}

            <div className="border-t pt-3">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div className="font-semibold text-xs">Riwayat Pembayaran ({payments.length})</div>
                {/* Feb 2026 — Bulk Void bar; muncul saat admin & invoice
                    eligible (LUNAS/SEBAGIAN) & ada payment ter-select */}
                {isAdmin && inv && (inv.status === "LUNAS" || inv.status === "SEBAGIAN") && voidSel.size > 0 && (
                  <div className="flex items-center gap-2 bg-[#EEF2FF] border border-[#C7D2FE] rounded px-2 py-1 text-xs"
                       data-testid="inv-bulk-void-bar">
                    <span className="font-semibold">{voidSel.size} pembayaran dipilih</span>
                    <Button size="sm" variant="destructive"
                            onClick={() => setBulkVoidOpen(true)}
                            data-testid="inv-bulk-void-btn">
                      Void Pembayaran
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => setVoidSel(new Set())}
                            data-testid="inv-bulk-void-clear">
                      Batal Pilih
                    </Button>
                  </div>
                )}
              </div>
              <ul className="space-y-2">
                {payments.length === 0 && <div className="text-xs text-[#6B7280]">Belum ada pembayaran.</div>}
                {payments.map((p) => {
                  const eligible = isAdmin
                    && p.status === "CONFIRMED"
                    && inv && (inv.status === "LUNAS" || inv.status === "SEBAGIAN");
                  const checked = voidSel.has(p.payment_id);
                  return (
                  <li key={p.payment_id} className={`text-xs p-2 rounded border ${p.status === "VOID" ? "bg-[#F3F4F6]" : "bg-white"} ${checked ? "ring-2 ring-[#C7D2FE]" : ""}`}
                      data-testid={`inv-detail-pay-${p.payment_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        {isAdmin && (
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!eligible}
                            onChange={(e) => {
                              setVoidSel((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(p.payment_id);
                                else next.delete(p.payment_id);
                                return next;
                              });
                            }}
                            title={eligible ? "Pilih untuk Bulk Void" : "Bulk Void hanya tersedia untuk pembayaran pada invoice LUNAS atau SEBAGIAN."}
                            aria-label={`Pilih payment ${p.payment_id} untuk Bulk Void`}
                            data-testid={`inv-void-select-${p.payment_id}`}
                          />
                        )}
                        <span className="font-mono">{p.payment_id}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${p.status === "VOID" ? "bg-[#E5E7EB]" : "bg-[#D1FAE5] text-[#065F46]"}`}>{p.status}</span>
                        {p.status === "CONFIRMED" && inv.status === "LUNAS" && Number(inv.sisa_tagihan) === 0 && onOpenReceipt && (
                          <Button size="sm" variant="outline"
                                  onClick={() => onOpenReceipt(inv.invoice_id)}
                                  disabled={openingReceipt}
                                  data-testid={`inv-detail-receipt-btn-${p.payment_id}`}
                                  title="Lihat kwitansi pembayaran" aria-label="Lihat kwitansi pembayaran">
                            <ReceiptText className="w-3 h-3 mr-1" />
                            {openingReceipt ? "Memuat…" : "PDF Kwitansi"}
                          </Button>
                        )}
                        {isAdmin && p.status === "CONFIRMED" && (
                          <Button size="sm" variant="outline" onClick={() => setEditMethodTarget(p)}
                                  data-testid={`inv-edit-method-btn-${p.payment_id}`}
                                  title="Ubah metode pembayaran" aria-label="Ubah metode pembayaran">
                            <Pencil className="w-3 h-3" />
                          </Button>
                        )}
                        {isAdmin && p.status === "CONFIRMED" && (
                          <Button size="sm" variant="outline" onClick={() => setVoidingId(p.payment_id)}
                                  data-testid={`inv-void-btn-${p.payment_id}`}
                                  title="Void Pembayaran" aria-label="Void Pembayaran">
                            <XCircle className="w-3 h-3" />
                          </Button>
                        )}
                      </span>
                    </div>
                    <div className={p.status === "VOID" ? "line-through" : ""}>
                      {p.payment_date} · {methodLabel(p.method)} · <b className="font-mono">{formatRupiah(p.amount_received)}</b>
                    </div>
                    <div className="text-[10px]" data-testid={`inv-received-${p.payment_id}`}>
                      Diterima oleh: <b>{receivedLabel(p).label}</b>
                    </div>
                    <div className="text-[10px]">Dicatat oleh: {p.recorded_by}</div>
                    {p.notes && <div className="italic">{p.notes}</div>}
                    {p.proof_url && (
                      <button type="button"
                              onClick={async () => {
                                try {
                                  const resp = await api.get(`/billing/payments/${p.payment_id}/proof`, { responseType: "blob" });
                                  const url = URL.createObjectURL(resp.data);
                                  window.open(url, "_blank", "noopener");
                                } catch (e) {
                                  toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal memuat bukti");
                                }
                              }}
                              className="mt-1 inline-flex items-center gap-1 text-[#0055FF] hover:underline"
                              data-testid={`inv-view-proof-${p.payment_id}`}>
                        <ImageIcon className="w-3 h-3" /> Lihat bukti transfer
                      </button>
                    )}
                    {p.status === "VOID" && (
                      <div className="text-[#991B1B]">
                        Void oleh {p.voided_by} · Alasan: {p.void_reason}
                      </div>
                    )}
                  </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {showDiscount && (
          <DiscountDialog invoice={inv} onClose={() => setShowDiscount(false)}
                          onSaved={async () => { setShowDiscount(false); await load(); onChanged?.(); }} />
        )}
        {showPay && (
          <AdminPaymentDialog invoice={inv} onClose={() => setShowPay(false)}
                               onSaved={async () => { setShowPay(false); await load(); onChanged?.(); }} />
        )}
        {showCancel && (
          <CancelInvoiceDialog invoice={inv} onClose={() => setShowCancel(false)}
                                onSaved={async () => { setShowCancel(false); await load(); onChanged?.(); }} />
        )}
        {voidingId && (
          <VoidPaymentDialog paymentId={voidingId} onClose={() => setVoidingId(null)}
                             onSaved={async () => { setVoidingId(null); await load(); onChanged?.(); }} />
        )}
        {editMethodTarget && (
          <EditPaymentMethodDialog payment={editMethodTarget}
                                     onClose={() => setEditMethodTarget(null)}
                                     onSaved={async () => { setEditMethodTarget(null); await load(); onChanged?.(); }} />
        )}
        {bulkVoidOpen && (
          <BulkVoidPaymentDialog
            payments={payments.filter((p) => voidSel.has(p.payment_id))}
            onClose={() => setBulkVoidOpen(false)}
            onDone={async () => { setBulkVoidOpen(false); await load(); onChanged?.(); }}
          />
        )}
        {itemFormOpen && (
          <InvoiceItemFormDialog
            invoice={inv}
            item={editingItem}
            onClose={() => { setItemFormOpen(false); setEditingItem(null); }}
            onSaved={async () => { setItemFormOpen(false); setEditingItem(null); await load(); onChanged?.(); }}
          />
        )}
        {deletingItemId && (
          <InvoiceItemDeleteDialog
            invoice={inv}
            item={(inv.items || []).find((x) => x.item_id === deletingItemId) || null}
            onClose={() => setDeletingItemId(null)}
            onDone={async () => { setDeletingItemId(null); await load(); onChanged?.(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ label, value, bold }) => (
  <div className="flex justify-between border-b py-1">
    <span className="text-[#6B7280]">{label}</span>
    <span className={`font-mono ${bold ? "font-bold" : ""}`}>{value}</span>
  </div>
);


function DiscountDialog({ invoice, onClose, onSaved }) {
  const [diskon, setDiskon] = useState(invoice?.diskon || 0);
  const [alasan, setAlasan] = useState(invoice?.alasan_diskon || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/billing/invoices/${invoice.invoice_id}/discount`, {
        diskon: parseInt(diskon, 10) || 0, alasan_diskon: alasan,
      });
      toast.success("Diskon berhasil diperbarui");
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal update diskon");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="discount-dialog">
        <DialogHeader><DialogTitle>Edit Diskon Invoice</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div className="text-xs">Harga paket: {formatRupiah(invoice?.harga_paket_snapshot)}</div>
          <div><Label>Nominal Diskon (Rp)</Label>
            <Input type="number" min="0" max={invoice?.harga_paket_snapshot || 0}
                   value={diskon} onChange={(e) => setDiskon(e.target.value)} required
                   data-testid="discount-amount" /></div>
          <div><Label>Alasan (opsional)</Label>
            <Textarea rows={2} value={alasan} onChange={(e) => setAlasan(e.target.value)} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={busy} data-testid="discount-submit">{busy ? "Menyimpan…" : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function CancelInvoiceDialog({ invoice, onClose, onSaved }) {
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/billing/invoices/${invoice.invoice_id}/cancel`, { alasan });
      toast.success("Invoice dibatalkan");
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal batalkan invoice");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="cancel-dialog">
        <DialogHeader><DialogTitle>Batalkan Invoice</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div className="text-xs">Apakah Anda yakin ingin membatalkan invoice <b className="font-mono">{invoice.invoice_id}</b>? Aksi ini akan mengubah status menjadi DIBATALKAN.</div>
          <div><Label>Alasan</Label>
            <Textarea rows={2} required minLength={3} value={alasan}
                      onChange={(e) => setAlasan(e.target.value)} data-testid="cancel-reason" /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={busy}>{busy ? "Memproses…" : "Konfirmasi Batalkan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function VoidPaymentDialog({ paymentId, onClose, onSaved }) {
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/billing/payments/${paymentId}/void`, { alasan });
      toast.success("Payment berhasil di-void. Invoice dihitung ulang.");
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal void payment");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="void-dialog">
        <DialogHeader><DialogTitle>Void Pembayaran</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div className="text-xs">Apakah Anda yakin membatalkan pembayaran <b className="font-mono">{paymentId}</b>? Nominal akan dikembalikan ke sisa tagihan invoice.</div>
          <div><Label>Alasan Void</Label>
            <Textarea rows={2} required minLength={3} value={alasan}
                      onChange={(e) => setAlasan(e.target.value)} data-testid="void-reason" /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={busy} data-testid="void-submit">{busy ? "Memproses…" : "Konfirmasi Void"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Feb 2026 — Admin ubah metode pembayaran tanpa VOID.
 *
 * Kasus pakai: koreksi data entry (misal kolektor pilih TUNAI padahal
 * nasabah bayar TRANSFER). Nominal invoice.total_dibayar tidak berubah.
 * Audit trail tersimpan di `payment.method_history`.
 *
 * Guard dari backend:
 *   - Hanya payment CONFIRMED yang bisa diubah.
 *   - Pindah ke TUNAI → customer wajib punya kolektor AKTIF.
 *   - Alasan minimal 3 karakter.
 */
function EditPaymentMethodDialog({ payment, onClose, onSaved }) {
  const [method, setMethod] = useState(payment.method || "TUNAI");
  const [alasan, setAlasan] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const unchanged = method === payment.method;
  const needProof = method === "TRANSFER" && !payment.proof_url;

  const submit = async (e) => {
    e.preventDefault();
    if (busy || unchanged) return;
    if (needProof && !proofFile) {
      toast.error("Bukti transfer wajib diunggah");
      return;
    }
    setBusy(true);
    try {
      let proof_url = undefined;
      if (needProof && proofFile) {
        const fd = new FormData();
        fd.append("file", proofFile);
        const up = await api.post("/billing/payments/proof-upload", fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        proof_url = up.data.proof_url;
      }
      await api.patch(`/billing/payments/${payment.payment_id}/method`,
        { method, alasan, proof_url });
      toast.success(`Metode berhasil diubah: ${payment.method} → ${method}`);
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal mengubah metode");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="edit-method-dialog">
        <DialogHeader><DialogTitle>Ubah Metode Pembayaran</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div className="rounded-md bg-[#F9FAFB] border p-3 text-xs space-y-1">
            <div><b>Payment:</b> <span className="font-mono">{payment.payment_id}</span></div>
            <div><b>Tanggal:</b> {payment.payment_date}</div>
            <div><b>Nominal:</b> <span className="font-mono font-semibold">{formatRupiah(payment.amount_received)}</span></div>
            <div><b>Metode saat ini:</b> <span className="px-1.5 py-0.5 rounded bg-white border font-mono text-[10px]">{payment.method}</span></div>
          </div>
          <div className="text-[11px] text-[#6B7280] leading-snug">
            Nominal pembayaran dan status invoice <b>tidak berubah</b>. Penerima akan diperbarui sesuai metode baru. Perubahan tercatat di riwayat payment (audit trail).
          </div>
          <div>
            <Label className="text-xs">Metode Baru</Label>
            <div className="flex gap-2 mt-1">
              {["TUNAI", "TRANSFER", "KANTOR"].map((m) => (
                <label key={m} className={`flex-1 cursor-pointer rounded-md border px-3 py-2 text-center text-sm transition
                  ${method === m ? "border-[#0055FF] bg-[#EFF6FF] font-semibold" : "border-[#E5E7EB] hover:bg-[#F9FAFB]"}`}
                  data-testid={`edit-method-opt-${m}`}>
                  <input type="radio" name="edit-method" value={m}
                         checked={method === m}
                         onChange={() => setMethod(m)} className="hidden" />
                  {m}
                </label>
              ))}
            </div>
          </div>
          {needProof && (
            <div>
              <Label className="text-xs">Bukti Transfer (wajib)</Label>
              <input type="file" accept="image/*,application/pdf"
                     onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                     className="block w-full text-xs mt-1"
                     data-testid="edit-method-proof" />
            </div>
          )}
          <div>
            <Label className="text-xs">Alasan Koreksi (wajib, min 3 char)</Label>
            <Textarea rows={2} required minLength={3} value={alasan}
                      onChange={(e) => setAlasan(e.target.value)}
                      placeholder="Mis: koreksi — pelanggan bayar via transfer bank, tercatat TUNAI karena salah pilih"
                      data-testid="edit-method-reason" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={busy || unchanged} data-testid="edit-method-submit">
              {busy ? "Memproses…" : unchanged ? "Metode sama" : "Simpan Perubahan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function AdminPaymentDialog({ invoice, onClose, onSaved }) {
  const sisa = parseInt(invoice.sisa_tagihan || 0, 10);
  // Kalau invoice sudah lunas, mode default PARTIAL supaya admin tetap
  // bisa mengetik nominal manual (misal koreksi/adjust nol) meski secara
  // umum backend menolak amount > sisa (sisa=0 → tidak bisa payment baru).
  const [payMode, setPayMode] = useState(sisa > 0 ? "FULL" : "PARTIAL");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("TUNAI");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState("");
  // Feb 2026 — Admin-only: tanggal pembayaran. Default hari ini (zona
  // waktu lokal browser). Dipakai untuk input pembayaran lama, koreksi
  // tanggal, atau migrasi historis. Dikirim mentah ke backend yang
  // menyimpan sebagai `payment_date` ("YYYY-MM-DD"). Backend sudah
  // menerima field ini sejak awal (optional, fallback = today UTC).
  // Rekap Billing memakai `payment_date` sebagai sumber periode payment.
  const todayLocal = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  })();
  const [paymentDate, setPaymentDate] = useState(todayLocal);

  const onProofFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setProofFile(null); setProofPreview(""); return; }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Ukuran maksimum 8 MB"); e.target.value = ""; return;
    }
    const ok = f.type.startsWith("image/") || f.type === "application/pdf";
    if (!ok) {
      toast.error("Format tidak didukung. Gunakan foto (JPG/PNG/WebP/HEIC) atau PDF.");
      e.target.value = ""; return;
    }
    setProofFile(f);
    setProofPreview(f.type.startsWith("image/") ? URL.createObjectURL(f) : "");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const effectiveAmount = payMode === "FULL" ? sisa : parseInt(amount, 10);
    if (payMode === "FULL") {
      if (sisa < 1) { toast.error("Invoice sudah lunas — tidak ada sisa untuk dibayar penuh"); return; }
    } else {
      if (!effectiveAmount || effectiveAmount < 1) {
        toast.error("Jumlah pembayaran wajib diisi (minimal Rp1)"); return;
      }
      if (sisa > 0 && effectiveAmount > sisa) {
        toast.error(`Melebihi sisa tagihan (${formatRupiah(sisa)})`); return;
      }
    }
    if (method === "TRANSFER" && !proofFile) {
      toast.error("Bukti transfer wajib diunggah untuk pembayaran TRANSFER");
      return;
    }
    // Feb 2026 — admin-only payment_date guard. Wajib terisi &
    // tidak boleh di masa depan. Format input "YYYY-MM-DD".
    if (!paymentDate || !/^\d{4}-\d{2}-\d{2}$/.test(paymentDate)) {
      toast.error("Tanggal pembayaran wajib diisi (YYYY-MM-DD)");
      return;
    }
    if (paymentDate > todayLocal) {
      toast.error("Tanggal pembayaran tidak boleh di masa depan");
      return;
    }
    setBusy(true);
    try {
      let proof_url = "";
      if (method === "TRANSFER" && proofFile) {
        const fd = new FormData();
        fd.append("file", proofFile);
        const up = await api.post("/billing/payments/proof-upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        proof_url = up.data.proof_url;
      }
      const payload = {
        invoice_id: invoice.invoice_id,
        method, notes: notes || "",
        proof_url: proof_url || undefined,
        payment_date: paymentDate || undefined,
      };
      if (payMode === "FULL") payload.pay_full = true;
      else payload.amount = effectiveAmount;
      await api.post("/billing/payments", payload);
      toast.success(`Pembayaran ${formatRupiah(effectiveAmount)} berhasil dicatat.`);
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal mencatat pembayaran");
    } finally { setBusy(false); }
  };

  const setMethodSafe = (m) => {
    setMethod(m);
    if (m !== "TRANSFER") { setProofFile(null); setProofPreview(""); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[95vw] max-w-lg sm:max-w-lg p-4 sm:p-6 max-h-[92dvh] overflow-y-auto" data-testid="admin-pay-dialog">
        <DialogHeader><DialogTitle>Catat Pembayaran (Admin)</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div className="text-xs bg-[#F8F9FA] border rounded p-2">
            <div className="font-semibold">{invoice.customer_name_snapshot}</div>
            <div className="font-mono text-[10px]">{invoice.invoice_id}</div>
            <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
              <div><div className="text-[#6B7280]">Total Tagihan</div><div className="font-mono font-semibold">{formatRupiah(invoice.total_harus_dibayar)}</div></div>
              <div><div className="text-[#6B7280]">Sudah Dibayar</div><div className="font-mono">{formatRupiah(invoice.total_dibayar)}</div></div>
              <div><div className="text-[#6B7280]">Sisa</div><div className="font-mono text-[#991B1B] font-semibold">{formatRupiah(invoice.sisa_tagihan)}</div></div>
            </div>
          </div>
          <div>
            <Label>Jenis Pembayaran</Label>
            <div className="flex gap-2 mt-1">
              {[
                { key: "FULL", label: "Bayar Penuh" },
                { key: "PARTIAL", label: "Bayar Sebagian" },
              ].map((m) => (
                <button type="button" key={m.key}
                        onClick={() => setPayMode(m.key)}
                        data-testid={`admin-paymode-${m.key}`}
                        disabled={m.key === "FULL" && sisa <= 0}
                        className={`flex-1 border rounded px-3 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${payMode === m.key ? "bg-[#0055FF] text-white border-[#0055FF]" : "bg-white text-[#111827]"}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Jumlah Diterima (Rp)</Label>
            {payMode === "FULL" ? (
              <div className="mt-1 border rounded px-3 py-2 bg-[#F3F4F6] font-mono font-semibold"
                   data-testid="admin-pay-amount-locked">
                {formatRupiah(sisa)}
              </div>
            ) : (
              <Input type="number" inputMode="numeric" min="1" required
                      value={amount} onChange={(e) => setAmount(e.target.value)}
                      autoFocus data-testid="admin-pay-amount" />
            )}
            <div className="text-[10px] text-[#6B7280] mt-1">
              {payMode === "FULL"
                ? "Otomatis mengikuti sisa tagihan saat ini."
                : `Sisa: ${formatRupiah(sisa)}. Backend menolak bila melebihi.`}
            </div>
          </div>
          <div><Label>Metode</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {["TUNAI", "TRANSFER", "KANTOR"].map((m) => (
                <label key={m} className={`border rounded px-2 py-2 cursor-pointer text-center text-xs sm:text-sm ${method === m ? "bg-[#0055FF] text-white" : ""}`}>
                  <input type="radio" name="admin-method" value={m} checked={method === m}
                          onChange={() => setMethodSafe(m)} className="hidden"
                          data-testid={`admin-pay-method-${m}`} />
                  {methodLabel(m)}
                </label>
              ))}
            </div>
            <div className="text-[10px] text-[#6B7280] mt-1">
              TUNAI = uang diberikan kepada kolektor. TRANSFER = ke rekening perusahaan (wajib bukti). LOKET = pembayaran diterima di titik/loket yang ditunjuk AROMA NET.
            </div>
          </div>

          {method === "TRANSFER" && (
            <div data-testid="admin-proof-block">
              <Label>Bukti Transfer <span className="text-[#991B1B]">*</span></Label>
              <div className="mt-1">
                {!proofFile ? (
                  <div className="grid grid-cols-2 gap-2" data-testid="admin-proof-drop">
                    <label className="flex items-center justify-center gap-2 border-2 border-dashed border-[#DBE4FF] rounded-md py-4 cursor-pointer text-[#0055FF] hover:bg-[#F0F4FF]">
                      <Camera className="w-4 h-4" />
                      <span className="text-xs font-semibold">Ambil Foto</span>
                      <input type="file" accept="image/*" capture="environment"
                              className="hidden" onChange={onProofFileChange}
                              data-testid="admin-proof-camera-input" />
                    </label>
                    <label className="flex items-center justify-center gap-2 border-2 border-dashed border-[#DBE4FF] rounded-md py-4 cursor-pointer text-[#0055FF] hover:bg-[#F0F4FF]">
                      <Upload className="w-4 h-4" />
                      <span className="text-xs font-semibold">Pilih Foto/PDF</span>
                      <input type="file" accept="image/*,application/pdf" className="hidden"
                             onChange={onProofFileChange} data-testid="admin-proof-input" />
                    </label>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 border rounded-md p-2 bg-[#F8F9FA]" data-testid="admin-proof-preview">
                    {proofPreview ? (
                      <img src={proofPreview} alt="Preview bukti" className="w-20 h-20 object-cover rounded border" />
                    ) : (
                      <div className="w-20 h-20 flex items-center justify-center rounded border bg-white text-[#6B7280]">
                        <FileText className="w-8 h-8" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1 text-xs">
                      <div className="font-semibold truncate">{proofFile.name}</div>
                      <div className="text-[#6B7280]">{(proofFile.size / 1024).toFixed(1)} KB · {proofFile.type || "-"}</div>
                      <button type="button" onClick={() => { setProofFile(null); setProofPreview(""); }}
                              className="mt-1 inline-flex items-center gap-1 text-[#991B1B] hover:underline"
                              data-testid="admin-proof-remove">
                        <X className="w-3 h-3" /> Hapus & pilih ulang
                      </button>
                    </div>
                  </div>
                )}
                <div className="text-[10px] text-[#6B7280] mt-1">
                  Foto struk / screenshot m-banking. Maks 8 MB. Format: JPG/PNG/WebP/HEIC/PDF.
                </div>
              </div>
            </div>
          )}

          <div><Label>Catatan Admin (opsional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Contoh: Diterima di loket / Titip melalui warung Bu Yanti / dsb"
                      data-testid="admin-pay-notes" /></div>

          <div>
            <Label>Tanggal Pembayaran <span className="text-[11px] text-[#6B7280] font-normal">(Admin)</span></Label>
            <Input type="date" value={paymentDate} max={todayLocal}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    data-testid="admin-pay-date" />
            <div className="text-[10px] text-[#6B7280] mt-1">
              Default hari ini. Ubah untuk input pembayaran lama / koreksi
              tanggal transaksi — Rekap Billing mengikuti tanggal ini.
              Tidak boleh di masa depan.
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Batal</Button>
            <Button type="submit" disabled={busy} data-testid="admin-pay-submit">
              {busy
                ? "Menyimpan…"
                : (() => {
                    const nominal = payMode === "FULL" ? sisa : parseInt(amount || 0, 10);
                    return nominal > 0 ? `Simpan Pembayaran ${formatRupiah(nominal)}` : "Simpan Pembayaran";
                  })()}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}



function CustomerPicker({ pageCollectorId, collectorList, pickerIds, setPickerIds, query, setQuery, list, setList, loading, setLoading, disabled }) {
  // Feb 2026 — In-dialog customer picker untuk mode "Berdasarkan Checklist".
  // SSoT filter kolektor = `pageCollectorId` dari halaman Invoice (dropdown
  // tidak di-duplikasi di dialog). Data via endpoint existing
  // `/billing/customers?status_layanan=AKTIF&collector_user_id=…&q=…`.
  //
  // BUG FIX Feb 2026: Pencarian "dewi" sebelumnya 0 hasil karena request
  // `limit=500` ditolak backend (`le=200` → HTTP 422) → catch → setList([]).
  // Fix: gunakan server-side search `q=` existing (regex case-insensitive
  // atas nama_customer/no_hp/customer_id) dengan debounce 300ms, dan limit
  // sesuai batas backend (200). Selection tetap tersimpan di parent
  // (`pickerIds` Set) sehingga tidak hilang saat search berubah.
  const [debouncedQ, setDebouncedQ] = useState(query || "");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ((query || "").trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = { status_layanan: "AKTIF", limit: 200, page: 1 };
        if (pageCollectorId) params.collector_user_id = pageCollectorId;
        if (debouncedQ) params.q = debouncedQ;
        const [custRes, pkgRes] = await Promise.all([
          api.get("/billing/customers", { params }),
          api.get("/billing/packages", { params: { limit: 200 } }),
        ]);
        if (!alive) return;
        const pkgs = (pkgRes.data?.items || pkgRes.data || []);
        const byId = new Map(pkgs.map((p) => [p.package_id, p]));
        const items = (custRes.data.items || []).map((c) => {
          const pkg = byId.get(c.package_id);
          return { ...c, _pkg_nama: pkg?.nama_paket || "—", _pkg_harga: pkg?.harga };
        });
        setList(items);
      } catch {
        if (alive) setList([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [pageCollectorId, debouncedQ, setList, setLoading]);

  const kolektorLabel = (() => {
    if (!pageCollectorId) return "Semua Kolektor";
    if (pageCollectorId === "KOLEKTOR-SYSTEM") return "Kolektor Sistem";
    return (collectorList.find((c) => c.id === pageCollectorId) || {}).name || "—";
  })();

  const q = query.trim().toLowerCase();
  const visibleList = q
    ? list.filter((c) =>
        (c.nama_customer || "").toLowerCase().includes(q)
        || (c.customer_id || "").toLowerCase().includes(q)
      )
    : list;

  const allVisibleSelected =
    visibleList.length > 0 && visibleList.every((c) => pickerIds.has(c.customer_id));

  const togglePickAllVisible = () => {
    setPickerIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleList.forEach((c) => next.delete(c.customer_id));
      } else {
        visibleList.forEach((c) => next.add(c.customer_id));
      }
      return next;
    });
  };

  const toggleOne = (cid) => {
    setPickerIds((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };

  return (
    <div className="mt-2 border border-[#E5E7EB] rounded-md p-2 bg-white" data-testid="gen-picker">
      <div className="text-xs font-semibold text-[#111827]">Customer yang akan dibuatkan invoice</div>
      <div className="text-[11px] text-[#6B7280] mt-0.5">
        Filter Kolektor: <b>{kolektorLabel}</b>
      </div>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Cari nama / Customer ID"
        className="mt-2 w-full border rounded-md px-2 py-1 text-sm"
        disabled={disabled}
        data-testid="gen-picker-search"
      />
      <label className="mt-2 flex items-center gap-2 text-xs cursor-pointer select-none">
        <input
          type="checkbox"
          checked={allVisibleSelected}
          onChange={togglePickAllVisible}
          disabled={disabled || visibleList.length === 0}
          data-testid="gen-picker-select-all"
        />
        <span>Pilih semua yang tampil ({visibleList.length})</span>
      </label>
      <div className="mt-1 max-h-48 overflow-y-auto rounded border border-[#F3F4F6] divide-y" data-testid="gen-picker-list">
        {loading ? (
          <div className="text-xs text-[#6B7280] px-2 py-3">Memuat customer…</div>
        ) : visibleList.length === 0 ? (
          <div className="text-xs text-[#6B7280] px-2 py-3">
            {list.length === 0 ? "Tidak ada customer AKTIF dalam filter kolektor ini." : "Tidak ada customer yang cocok dengan pencarian."}
          </div>
        ) : (
          visibleList.map((c) => (
            <label key={c.customer_id}
                   className="flex items-start gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-[#F8FAFC]"
                   data-testid={`gen-picker-row-${c.customer_id}`}>
              <input
                type="checkbox"
                className="mt-0.5"
                checked={pickerIds.has(c.customer_id)}
                onChange={() => toggleOne(c.customer_id)}
                disabled={disabled}
                data-testid={`gen-picker-check-${c.customer_id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] text-[#6B7280]">{c.customer_id}</div>
                <div className="truncate font-medium">{c.nama_customer}</div>
                <div className="text-[10px] text-[#374151]">
                  {c._pkg_nama}
                  {typeof c._pkg_harga === "number" && <> · {formatRupiah(c._pkg_harga)}</>}
                </div>
              </div>
            </label>
          ))
        )}
      </div>
      <div className="mt-1 text-[11px] text-[#6B7280]" data-testid="gen-picker-counter">
        {list.length} customer tersedia · <b>{pickerIds.size}</b> dipilih
      </div>
    </div>
  );
}



function BulkVoidPaymentDialog({ payments, onClose, onDone }) {
  // Feb 2026 — Confirmation dialog untuk Bulk Void Payment. Satu alasan
  // dipakai utk semua payment (identik dgn pola Void SINGLE). Backend
  // menolak non-eligible per-payment (partial success).
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const ids = payments.map((p) => p.payment_id);
  const total = payments.reduce((s, p) => s + Number(p.amount_received || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (alasan.trim().length < 3) {
      toast.error("Alasan void minimal 3 karakter.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/payments/bulk-void", {
        payment_ids: ids,
        alasan: alasan.trim(),
      });
      setResult(data);
      const s = data?.succeeded ?? 0;
      const sk = data?.skipped_count ?? 0;
      if (s > 0 && sk === 0) toast.success(`${s} pembayaran di-void.`);
      else if (s > 0 && sk > 0) toast.success(`${s} di-void, ${sk} dilewati.`);
      else toast.warning("Tidak ada pembayaran yang di-void.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal bulk void.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && (result ? onDone?.() : onClose())}>
      <DialogContent className="w-[95vw] max-w-md" data-testid="bulk-void-dialog">
        <DialogHeader>
          <DialogTitle>Void Pembayaran</DialogTitle>
        </DialogHeader>
        {result ? (
          <div className="space-y-2 text-sm">
            <div className="rounded-md bg-[#F3F4F6] p-3">
              <div className="font-semibold mb-1">Bulk Void Pembayaran Selesai</div>
              <div>Berhasil: <b data-testid="bulk-void-succeeded">{result.succeeded ?? 0}</b></div>
              <div>Tidak diproses: <b data-testid="bulk-void-skipped">{result.skipped_count ?? 0}</b></div>
            </div>
            {Array.isArray(result.skipped) && result.skipped.length > 0 && (
              <div className="text-xs">
                <div className="font-semibold mb-1">Detail yang dilewati</div>
                <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                  {result.skipped.map((s) => (
                    <li key={s.payment_id} className="font-mono">
                      {s.payment_id} — <span className="text-[#991B1B]">{s.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <DialogFooter>
              <Button type="button" onClick={onDone} data-testid="bulk-void-close">Tutup</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 text-sm" noValidate>
            <div className="rounded-md bg-[#FEF2F2] border border-[#FCA5A5] px-3 py-2 text-[13px]">
              Anda akan membatalkan <b>{ids.length} transaksi pembayaran</b> (total {formatRupiah(total)}).
              Pembayaran yang di-void tidak lagi dihitung sebagai pembayaran efektif pada invoice terkait.
              Invoice tetap ada dan statusnya akan dihitung kembali.
            </div>
            <div>
              <Label htmlFor="bulk-void-reason">Alasan</Label>
              <Textarea
                id="bulk-void-reason"
                rows={2}
                value={alasan}
                onChange={(e) => setAlasan(e.target.value)}
                placeholder="Alasan void (akan diterapkan ke semua payment)"
                minLength={3}
                maxLength={500}
                disabled={busy}
                data-testid="bulk-void-reason"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Batal</Button>
              <Button type="submit" variant="destructive" disabled={busy || alasan.trim().length < 3}
                      data-testid="bulk-void-submit">
                {busy ? "Memproses…" : `Void Pembayaran (${ids.length})`}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}


// Feb 2026 — Dialog Add/Edit Item Tambahan.
// Nama bebas (free-text), nominal Rupiah ≥ 0. Backend validate ulang.
function InvoiceItemFormDialog({ invoice, item, onClose, onSaved }) {
  const [name, setName] = useState(item?.name || "");
  const [amount, setAmount] = useState(item?.amount ?? 0);
  const [busy, setBusy] = useState(false);
  const isEdit = !!item;
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const trimmed = (name || "").trim();
    if (!trimmed) { toast.error("Nama item wajib diisi"); return; }
    if (trimmed.length > 200) { toast.error("Nama item maksimal 200 karakter"); return; }
    const amt = parseInt(amount, 10);
    if (!Number.isFinite(amt) || amt < 0) { toast.error("Jumlah harus angka ≥ 0"); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/billing/invoices/${invoice.invoice_id}/items/${item.item_id}`,
                        { name: trimmed, amount: amt });
        toast.success("Item diperbarui");
      } else {
        await api.post(`/billing/invoices/${invoice.invoice_id}/items`,
                       { name: trimmed, amount: amt });
        toast.success("Item ditambahkan");
      }
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal menyimpan item");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="inv-item-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Item" : "Tambah Item"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div>
            <Label htmlFor="inv-item-name">Nama Item</Label>
            <Input id="inv-item-name" type="text" maxLength={200}
                   placeholder="Contoh: Router, ONU, Kabel UTP 20m, Jasa Instalasi…"
                   value={name} onChange={(e) => setName(e.target.value)}
                   required autoFocus data-testid="inv-item-form-name" />
            <div className="text-[10px] text-[#6B7280] mt-1">
              Nama bebas sesuai kebutuhan invoice (bukan daftar master). Maks 200 karakter.
            </div>
          </div>
          <div>
            <Label htmlFor="inv-item-amount">Jumlah (Rp)</Label>
            <Input id="inv-item-amount" type="number" min="0" step="1"
                   value={amount} onChange={(e) => setAmount(e.target.value)}
                   required data-testid="inv-item-form-amount" />
          </div>
          <div className="text-[11px] text-[#6B7280] bg-[#F8FAFC] border rounded p-2">
            Jumlah item akan menambah Total Tagihan invoice. Pembayaran yang
            sudah tercatat tidak akan diubah; sisa &amp; status di-recompute
            otomatis.
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={busy} data-testid="inv-item-form-submit">
              {busy ? "Menyimpan…" : (isEdit ? "Simpan Perubahan" : "Tambah Item")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// Feb 2026 — Konfirmasi hapus item.
function InvoiceItemDeleteDialog({ invoice, item, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  if (!item) return null;
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.delete(`/billing/invoices/${invoice.invoice_id}/items/${item.item_id}`);
      toast.success("Item dihapus");
      await onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal menghapus item");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="inv-item-delete-dialog">
        <DialogHeader><DialogTitle>Hapus Item</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            Hapus item <b>{item.name}</b> (<span className="font-mono">{formatRupiah(item.amount)}</span>)
            dari invoice <b className="font-mono">{invoice.invoice_id}</b>?
          </div>
          <div className="text-[11px] text-[#6B7280] bg-[#F8FAFC] border rounded p-2">
            Total Tagihan akan berkurang. Pembayaran existing tidak diubah.
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="button" variant="destructive" onClick={submit} disabled={busy}
                    data-testid="inv-item-delete-confirm">
              {busy ? "Menghapus…" : "Hapus"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}




// Feb 2026 — Bulk Void Payment Multi Invoice (ADMIN ONLY).
// Flow: (1) fetch dry_run preview saat dialog dibuka, (2) tampilkan
// ringkasan + daftar skipped, (3) admin isi alasan + konfirmasi, (4)
// POST real → tampilkan hasil, (5) onDone refresh list & clear selection.
// Business logic reuse `_void_single_payment()` di backend — frontend
// hanya orchestrate UI.
function BulkVoidMultiInvoiceDialog({ invoiceIds, onClose, onDone }) {
  const [stage, setStage] = useState("loading");  // loading|preview|processing|result|error
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [alasan, setAlasan] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/billing/payments/bulk-void-multi-invoice", {
          invoice_ids: invoiceIds,
          alasan: "preview",  // dummy, backend tidak void saat dry_run
          dry_run: true,
        });
        if (!cancelled) {
          setPreview(data);
          setStage("preview");
        }
      } catch (err) {
        if (!cancelled) {
          setError(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal memuat preview");
          setStage("error");
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const a = (alasan || "").trim();
    if (a.length < 3) { toast.error("Alasan minimal 3 karakter"); return; }
    setStage("processing");
    try {
      const { data } = await api.post("/billing/payments/bulk-void-multi-invoice", {
        invoice_ids: invoiceIds,
        alasan: a,
        dry_run: false,
      });
      setResult(data);
      setStage("result");
      if ((data.payment_count_voided || 0) > 0) {
        toast.success(`${data.payment_count_voided} pembayaran berhasil di-void (Rp${Number(data.total_voided_amount || 0).toLocaleString("id-ID")})`);
      } else {
        toast.info("Tidak ada pembayaran yang di-void (semua di-skip)");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal void pembayaran");
      setStage("error");
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v && stage !== "processing") onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="bulk-void-multi-dialog">
        <DialogHeader>
          <DialogTitle>Void Pembayaran Terpilih</DialogTitle>
        </DialogHeader>

        {stage === "loading" && (
          <div className="text-sm text-[#6B7280] py-6 text-center">Memuat preview…</div>
        )}

        {stage === "error" && (
          <div className="space-y-3 text-sm">
            <div className="text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded p-3"
                 data-testid="bulk-void-multi-error">{error}</div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Tutup</Button>
            </DialogFooter>
          </div>
        )}

        {stage === "preview" && preview && (
          <form onSubmit={submit} className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="border rounded p-2 bg-[#F8FAFC]">
                <div className="text-[#6B7280]">Invoice dipilih</div>
                <div className="font-bold text-base" data-testid="bulk-void-multi-selected">{preview.invoice_count_selected}</div>
              </div>
              <div className="border rounded p-2 bg-[#ECFDF5]">
                <div className="text-[#065F46]">Invoice eligible</div>
                <div className="font-bold text-base text-[#065F46]" data-testid="bulk-void-multi-eligible">{preview.invoice_count_eligible}</div>
              </div>
              <div className="border rounded p-2 bg-[#FEF2F2]">
                <div className="text-[#991B1B]">Invoice dilewati</div>
                <div className="font-bold text-base text-[#991B1B]" data-testid="bulk-void-multi-skipped">{preview.invoice_count_skipped}</div>
              </div>
              <div className="border rounded p-2 bg-[#FFF7ED]">
                <div className="text-[#9A3412]">Payment akan di-void</div>
                <div className="font-bold text-base text-[#9A3412]" data-testid="bulk-void-multi-planned">{preview.payment_count_planned}</div>
              </div>
            </div>
            <div className="border rounded p-2 bg-[#FFFBEB]">
              <div className="text-[#92400E] text-xs">Total nominal payment yang akan di-void</div>
              <div className="font-bold font-mono text-lg text-[#92400E]"
                   data-testid="bulk-void-multi-total-amount">
                {formatRupiah(preview.total_planned_amount)}
              </div>
            </div>

            {preview.preview?.length > 0 && (
              <details className="border rounded" open>
                <summary className="cursor-pointer px-2 py-1 bg-[#F8FAFC] text-xs font-semibold">
                  Daftar invoice eligible ({preview.preview.length})
                </summary>
                <ul className="max-h-40 overflow-auto p-2 space-y-1 text-xs"
                    data-testid="bulk-void-multi-preview-list">
                  {preview.preview.map((p) => (
                    <li key={p.invoice_id} className="flex justify-between gap-2 border-b pb-1 last:border-0">
                      <span className="font-mono truncate">{p.invoice_id}</span>
                      <span className="shrink-0">
                        <Badge className="text-[10px] mr-1">{p.status}</Badge>
                        {p.payment_count} pay · <b className="font-mono">{formatRupiah(p.total)}</b>
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {preview.invoices_skipped?.length > 0 && (
              <details className="border rounded">
                <summary className="cursor-pointer px-2 py-1 bg-[#FEF2F2] text-xs font-semibold text-[#991B1B]">
                  Invoice dilewati ({preview.invoices_skipped.length})
                </summary>
                <ul className="max-h-32 overflow-auto p-2 space-y-1 text-[11px]"
                    data-testid="bulk-void-multi-skipped-list">
                  {preview.invoices_skipped.map((s, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span className="font-mono truncate">{s.invoice_id}</span>
                      <span className="shrink-0 text-[#991B1B]">{s.reason}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            <div>
              <Label htmlFor="bulk-void-multi-reason">Alasan (wajib, min 3 karakter)</Label>
              <Textarea id="bulk-void-multi-reason" rows={2} required minLength={3}
                        placeholder="Misal: Koreksi batch pembayaran September"
                        value={alasan} onChange={(e) => setAlasan(e.target.value)}
                        data-testid="bulk-void-multi-reason" />
            </div>

            <div className="text-[11px] text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] rounded p-2">
              Pembayaran yang dipilih akan menjadi <b>VOID</b> dan tidak lagi dihitung sebagai
              pembayaran efektif. Invoice tetap tersimpan dan statusnya akan dihitung ulang
              oleh engine existing.
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
              <Button type="submit" variant="destructive"
                      disabled={preview.payment_count_planned === 0}
                      data-testid="bulk-void-multi-submit">
                Konfirmasi Void Pembayaran ({preview.payment_count_planned})
              </Button>
            </DialogFooter>
          </form>
        )}

        {stage === "processing" && (
          <div className="text-sm text-[#6B7280] py-6 text-center">
            Memproses void pembayaran… jangan tutup dialog.
          </div>
        )}

        {stage === "result" && result && (
          <div className="space-y-3 text-sm">
            <div className="border rounded p-3 bg-[#ECFDF5]">
              <div className="font-semibold text-[#065F46]">Bulk Void selesai</div>
              <div className="text-xs mt-1">
                Berhasil: <b data-testid="bulk-void-multi-done-voided">{result.payment_count_voided}</b> payment
                {" · "}
                <span className="font-mono" data-testid="bulk-void-multi-done-amount">
                  {formatRupiah(result.total_voided_amount)}
                </span>
              </div>
              {result.payment_count_skipped > 0 && (
                <div className="text-xs text-[#92400E] mt-1">
                  Payment dilewati saat eksekusi: {result.payment_count_skipped}
                </div>
              )}
              {result.invoice_count_skipped > 0 && (
                <div className="text-xs text-[#991B1B] mt-1">
                  Invoice dilewati: {result.invoice_count_skipped}
                </div>
              )}
            </div>

            {result.payments_skipped?.length > 0 && (
              <details className="border rounded" open>
                <summary className="cursor-pointer px-2 py-1 bg-[#FEF2F2] text-xs font-semibold text-[#991B1B]">
                  Payment dilewati ({result.payments_skipped.length})
                </summary>
                <ul className="max-h-32 overflow-auto p-2 space-y-1 text-[11px]">
                  {result.payments_skipped.map((s, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span className="font-mono truncate">{s.payment_id}</span>
                      <span className="shrink-0 text-[#991B1B]">{s.reason}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {result.invoices_skipped?.length > 0 && (
              <details className="border rounded">
                <summary className="cursor-pointer px-2 py-1 bg-[#FEF2F2] text-xs font-semibold text-[#991B1B]">
                  Invoice dilewati ({result.invoices_skipped.length})
                </summary>
                <ul className="max-h-32 overflow-auto p-2 space-y-1 text-[11px]">
                  {result.invoices_skipped.map((s, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span className="font-mono truncate">{s.invoice_id}</span>
                      <span className="shrink-0 text-[#991B1B]">{s.reason}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            <DialogFooter>
              <Button type="button" onClick={onDone} data-testid="bulk-void-multi-close">Tutup</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

